import { useCallback, useEffect, useRef, useState } from "react";
import {
  ASPIRATOR_ROUTE_COLOR,
  buildRouteMapStopKey as buildRouteMapStopKeyFromData,
  buildRouteSequenceData as buildRouteSequenceDataFromData,
  buildTripTimelineSegments,
  buildExactRoutePolylineSegments as buildExactRoutePolylineSegmentsFromData,
  buildRoutePolylineSegments as buildRoutePolylineSegmentsFromData,
  collectRouteLatLngs as collectRouteLatLngsFromData,
  collectRouteSegmentLatLngGroups as collectRouteSegmentLatLngGroupsFromData,
  collectStopsWithCoordinates as collectStopsWithCoordinatesFromData,
  DELIVERY_ROUTE_COLOR,
  DEPOT_ROUTE_COLOR,
  flattenRouteSegmentLatLngGroups as flattenRouteSegmentLatLngGroupsFromData,
  getRouteDiagnostic as getRouteDiagnosticFromData,
  getTripRouteSegments as getTripRouteSegmentsFromData,
  getTripRoutableStops as getTripRoutableStopsFromData,
  LOADING_ROUTE_COLOR,
} from "../utils/routeModelsData.js";
import { collectAspiratorMissionRouteSegmentIndexes } from "../utils/fleetTimelinePresentation.js";
import { getClientSelectionId as getClientSelectionIdFromFleet } from "../utils/resultsFleetData.js";
import { attachRouteMapBasemap } from "../utils/routeMapBasemap.js";

const DEFAULT_CENTER = [46.0, -72.5];
const DEFAULT_ZOOM = 7;
const DEFAULT_PADDING = [28, 28];
const STOPS_ONLY_PADDING = [36, 36];
/** Zoom ville pour un seul point (ex. préchargement « vous êtes ici ») — évite le zoom rue de fitBounds. */
const SINGLE_STOP_FOCUS_ZOOM = 11;
const DETACHED_MAP_DRAG_THRESHOLD_PX = 4;
const STOP_BADGE_CLUSTER_ICON_SIZE = 68;
const STOP_BADGE_CLUSTER_DISABLE_ZOOM = 15;
const STOP_BADGE_CLUSTER_RADIUS = 26;
const STOP_BADGE_CLUSTER_PREVIEW_MAX = 8;
const STOP_BADGE_CLUSTER_PREVIEW_RADIUS = 20;
const STOP_BADGE_SPIDERFY_DISTANCE_MULTIPLIER = 1.35;
const ICON_TEXT_BADGE_WIDTH = 104;
const ICON_TEXT_BADGE_HEIGHT = 30;
const MOBILE_ICON_BADGE_EDGE_INSET = 2;
const MOBILE_ICON_BADGE_POINT_GAP = 4;
const ICON_BADGE_ROUTE_CLEARANCE_PX = 12;
const ICON_BADGE_OBSTACLE_MARGIN_PX = 4;
const ICON_BADGE_PLACEMENT_CANDIDATES = [
  "top",
  "bottom",
  "left",
  "right",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];
const ROUTE_DISTANCE_LABEL_Z_INDEX_OFFSET = 900;
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const XLINK_NAMESPACE = "http://www.w3.org/1999/xlink";
const ROUTE_SEGMENT_DIRECTION_ANIMATION_DURATION_SECONDS = 6.4;
const ROUTE_SEGMENT_DIRECTION_ARROW_COUNT = 4;

let routeSegmentPathIdSequence = 0;

function isHtmlElement(value) {
  return Boolean(value && typeof value === "object" && value.nodeType === 1 && value.ownerDocument);
}

function resolveElementWindow(element) {
  if (isHtmlElement(element) && element.ownerDocument?.defaultView) {
    return element.ownerDocument.defaultView;
  }

  return typeof window === "undefined" ? null : window;
}

function resolveMapHostWindow(mapState) {
  if (!mapState) {
    return typeof window === "undefined" ? null : window;
  }

  const mapContainer = typeof mapState.map?.getContainer === "function"
    ? mapState.map.getContainer()
    : mapState.mapContainer;

  return resolveElementWindow(mapContainer);
}

function shouldUseDetachedMapPointerPan(mapElement) {
  return isHtmlElement(mapElement)
    && typeof document !== "undefined"
    && mapElement.ownerDocument !== document;
}

function shouldIgnoreDetachedMapPanTarget(target) {
  return isHtmlElement(target) && typeof target.closest === "function" && Boolean(target.closest(
    ".leaflet-control, .leaflet-marker-icon, .route-map-stop-index, .route-map-stop-cluster__preview, .leaflet-popup, .leaflet-tooltip",
  ));
}

function isPrimaryPointerButtonPressed(event) {
  return typeof event?.buttons !== "number" || (event.buttons & 1) === 1;
}

function parseHexColor(color) {
  if (typeof color !== "string") {
    return null;
  }

  const normalized = color.trim().replace(/^#/, "");
  const expanded = normalized.length === 3
    ? normalized.split("").map((char) => `${char}${char}`).join("")
    : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return null;
  }

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

function blendHexColor(color, mixColor = "#ffffff", amount = 0.18) {
  const source = parseHexColor(color);
  const mix = parseHexColor(mixColor);
  if (!source || !mix) {
    return color;
  }

  const normalizedAmount = Math.max(0, Math.min(1, amount));
  const channel = (left, right) => Math.round(left + ((right - left) * normalizedAmount));
  return `#${[channel(source.r, mix.r), channel(source.g, mix.g), channel(source.b, mix.b)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

function getRouteDiagnostic(route) {
  return getRouteDiagnosticFromData(route);
}

function getRouteStatusMeta(route, diagnostic) {
  if (!route || !route.status) {
    return { label: "Routage indisponible", tone: "neutral" };
  }

  if (route.status === "success") {
    if (!route.geometry_available && diagnostic.error_type === "geometry_missing") {
      return { label: "Géométrie manquante", tone: "warning" };
    }

    return { label: "", tone: "success", isHidden: true };
  }

  if (route.status === "error") {
    if (diagnostic.error_type === "address_invalid") {
      return { label: "Adresse non routable", tone: "warning" };
    }

    if (diagnostic.error_type === "coordinates_invalid") {
      return { label: "Coordonnées invalides", tone: "warning" };
    }

    if (diagnostic.error_type === "quota") {
      return { label: "Routage saturé", tone: "danger" };
    }

    if (diagnostic.error_type === "timeout") {
      return { label: "Routage trop lent", tone: "danger" };
    }

    if (diagnostic.error_type === "empty_response") {
      return { label: "Routage sans réponse", tone: "danger" };
    }

    if (diagnostic.error_type === "parse_error" || diagnostic.error_type === "backend_processing") {
      return { label: "Trajet illisible", tone: "danger" };
    }

    if (diagnostic.error_type === "service_unavailable") {
      return { label: "Service de routage indisponible", tone: "danger" };
    }

    return { label: "Erreur routage", tone: "danger" };
  }

  if (route.error_code === "missing_coordinates") {
    return { label: "Coordonnées manquantes", tone: "warning" };
  }

  if (route.error_code === "not_enough_waypoints") {
    return { label: "Points insuffisants", tone: "warning" };
  }

  return { label: "Routage non tenté", tone: "neutral" };
}

function buildRouteStatusNotice(trip, route, diagnostic, mapRenderMeta) {
  if (!trip) {
    return {
      hidden: false,
      title: "Carte en attente",
      message: "Choisissez un camion puis une tournée pour afficher la route ou le diagnostic associé.",
    };
  }

  if (!mapRenderMeta) {
    return {
      hidden: false,
      title: "Carte en attente",
      message: "La carte se prépare pour la tournée sélectionnée.",
    };
  }

  if (mapRenderMeta.rendered) {
    return {
      hidden: true,
      title: "",
      message: "",
    };
  }

  if (mapRenderMeta.category === "no-map") {
    return {
      hidden: false,
      title: "Carte indisponible",
      message: "La carte n'a pas pu se charger. Rechargez la page.",
    };
  }

  if (mapRenderMeta.category === "frontend") {
    return {
      hidden: false,
      title: "Tracé non affiché",
      message: "Le tracé du trajet n'a pas pu s'afficher sur la carte.",
    };
  }

  if (route?.error_message) {
    const title = diagnostic.interpretation || (
      diagnostic.failure_category === "service"
        ? "Échec du service de routage"
        : diagnostic.failure_category === "data"
          ? "Route bloquée par les données"
          : "Échec du routage"
    );
    const markerMessage = mapRenderMeta.markerCount
      ? ` Les ${mapRenderMeta.markerCount} point(s) de la tournée restent visibles sur la carte.`
      : "";
    const actionMessage = diagnostic.action ? ` ${diagnostic.action}` : "";

    return {
      hidden: false,
      title,
      message: `${route.error_message}${actionMessage}${markerMessage}`,
    };
  }

  if (typeof diagnostic.missing_coordinates_count === "number" && diagnostic.missing_coordinates_count > 0) {
    return {
      hidden: false,
      title: "Coordonnées manquantes",
      message: "Au moins un arrêt n'a pas de coordonnées exploitables. La requête de routage n'a donc pas été envoyée.",
    };
  }

  if (mapRenderMeta.category === "stops-only") {
    if (mapRenderMeta.markerCount > 0) {
      return {
        hidden: true,
        title: "",
        message: "",
      };
    }

    return {
      hidden: false,
      title: "Trace de trajet absente",
      message: "Les points de la tournée sont visibles, mais aucune géométrie exploitable n'est disponible pour cette sélection.",
    };
  }

  if (mapRenderMeta.category === "no-coordinates") {
    return {
      hidden: false,
      title: "Aucune coordonnée exploitable",
      message: "La sélection courante ne fournit aucun point exploitable pour tracer ou centrer la carte.",
    };
  }

  return {
    hidden: false,
    title: "Carte prête pour le trajet",
    message: "La géométrie du trajet sélectionné s'affiche ici lorsqu'elle est disponible.",
  };
}

function buildDefaultMapRenderMeta() {
  return {
    rendered: false,
    category: "no-trip",
    displayLabel: "Carte en attente",
    routePointCount: 0,
    markerCount: 0,
  };
}

function hasStopCoordinates(stop) {
  return Boolean(stop && typeof stop.latitude === "number" && typeof stop.longitude === "number");
}

function isStopWarning(stop) {
  if (!stop || typeof stop !== "object") {
    return false;
  }

  return Boolean(
    stop.warning
    || stop.has_warning
    || stop.warning_message
    || stop.address_action_required
    || stop.severity === "warning",
  );
}

function getStopDisplayColor(stop) {
  if (isStopWarning(stop)) {
    return "#d97706";
  }

  if (stop?.stop_type === "loading") {
    return LOADING_ROUTE_COLOR;
  }

  if (stop?.stop_type === "delivery") {
    return DELIVERY_ROUTE_COLOR;
  }

  if (stop?.stop_type === "depot_return") {
    return DEPOT_ROUTE_COLOR;
  }

  return DEPOT_ROUTE_COLOR;
}

function getStopTypeLabel(stop) {
  if (stop?.stop_type === "depot_start") {
    return "Départ entrepôt";
  }

  if (stop?.stop_type === "loading") {
    return stop.order_id ? `Chargement ${stop.order_id}` : "Chargement";
  }

  if (stop?.stop_type === "depot_return") {
    return "Retour entrepôt";
  }

  return stop?.order_id ? `Livraison ${stop.order_id}` : "Livraison";
}

function buildStopTooltipLabel(stop, routeLabel = "") {
  const stopTypeLabel = getStopTypeLabel(stop);
  const stopLabel = stop && (stop.label || stop.order_id)
    ? stop.label || stop.order_id
    : stopTypeLabel;
  const baseLabel = stopLabel === stopTypeLabel ? stopLabel : `${stopLabel} - ${stopTypeLabel}`;
  const normalizedRouteLabel = typeof routeLabel === "string" ? routeLabel.trim() : "";
  return normalizedRouteLabel ? `Route ${normalizedRouteLabel} - ${baseLabel}` : baseLabel;
}

function getRouteNumberForStopIndex(index) {
  if (!Number.isInteger(index) || index <= 0) {
    return null;
  }

  return index;
}

function buildRouteMapStopKey(stop, fallbackIndex = null) {
  return buildRouteMapStopKeyFromData(stop, { fallbackIndex });
}

export function buildRouteBadgeMetaByStopKeyFromSequenceItems(routeSequenceItems = [], {
  showGroupedStopRangeLabels = false,
} = {}) {
  const routeBadgeMetaByStopKey = new Map();

  (Array.isArray(routeSequenceItems) ? routeSequenceItems : []).forEach((item) => {
    const stopKey = typeof item?.mapAnchorStopKey === "string"
      ? item.mapAnchorStopKey.trim()
      : "";
    const routeNumber = Number(item?.routeSequenceDisplaySequence ?? item?.globalSequence);
    const groupStartSequence = Number(item?.sameSiteGroupDisplayStartSequence ?? item?.sameSiteGroupStartSequence);
    const groupEndSequence = Number(item?.sameSiteGroupDisplayEndSequence);
    const groupSize = Number(item?.sameSiteGroupSize);
    const isGroupedStop = Number.isInteger(groupStartSequence)
      && groupStartSequence > 0
      && Number.isInteger(groupSize)
      && groupSize > 1;
    const resolvedGroupEndSequence = Number.isInteger(groupEndSequence) && groupEndSequence > 0
      ? groupEndSequence
      : (isGroupedStop ? groupStartSequence + groupSize - 1 : 0);
    const anchorRouteNumber = isGroupedStop ? groupStartSequence : routeNumber;
    const usesGroupedRangeLabel = isGroupedStop && showGroupedStopRangeLabels;
    const routeLabel = isGroupedStop
      ? usesGroupedRangeLabel
        ? `${groupStartSequence}-${resolvedGroupEndSequence}`
        : String(groupStartSequence)
      : (Number.isInteger(routeNumber) && routeNumber > 0 ? String(routeNumber) : "");
    const shouldRenderBadge = item?.isTerminalRouteMarker !== true
      && (!isGroupedStop || (Number(item?.sameSiteGroupIndex) || 0) === 0);

    if (!stopKey || !Number.isInteger(anchorRouteNumber) || anchorRouteNumber <= 0 || routeBadgeMetaByStopKey.has(stopKey)) {
      return;
    }

    routeBadgeMetaByStopKey.set(stopKey, {
      routeLabel,
      routeNumber: anchorRouteNumber,
      shouldRenderBadge,
      isGroupedStop,
      usesGroupedRangeLabel,
    });
  });

  return routeBadgeMetaByStopKey;
}

function buildRouteBadgeMetaByStopKey(assignment, trip, {
  showGroupedStopRangeLabels = false,
} = {}) {
  const routeSequenceModel = buildRouteSequenceDataFromData(null, assignment, trip);
  const routeSequenceItems = Array.isArray(routeSequenceModel?.items) ? routeSequenceModel.items : [];
  return buildRouteBadgeMetaByStopKeyFromSequenceItems(routeSequenceItems, {
    showGroupedStopRangeLabels,
  });
}

function buildRouteBadgeClassName(routeNumber = null) {
  const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
  return Number.isInteger(normalizedRouteNumber)
    ? `route-map-stop-index route-map-stop-index--anchor-${normalizedRouteNumber}`
    : "route-map-stop-index";
}

function buildStopBadgeClusterRouteLabel(marker) {
  const routeLabel = typeof marker?.__routeLabel === "string" ? marker.__routeLabel.trim() : "";

  if (routeLabel) {
    return routeLabel;
  }

  const routeNumber = marker?.__routeNumber;
  return Number.isInteger(routeNumber) ? String(routeNumber) : "";
}

function getRouteBadgeIconLabel(stopType = "") {
  switch (stopType) {
    case "loading":
      return "Provenance";
    case "delivery":
      return "Destination";
    case "depot_start":
      return "Départ";
    case "depot_return":
      return "Retour";
    default:
      return "Étape";
  }
}

function buildRouteBadgeIconContent(stop = null, stopType = "") {
  const normalizedBadgeRole = typeof stop?.badge_role === "string" ? stop.badge_role.trim() : "";
  const normalizedStopType = typeof stopType === "string" && stopType.trim()
    ? stopType.trim()
    : (typeof stop?.stop_type === "string" ? stop.stop_type.trim() : "");
  const fallbackLabel = getRouteBadgeIconLabel(normalizedStopType);
  const normalizedLabel = typeof stop?.label === "string" ? stop.label.trim() : "";
  const normalizedClientName = typeof stop?.client_name === "string" ? stop.client_name.trim() : "";
  const normalizedAddress = typeof stop?.address === "string" ? stop.address.trim() : "";
  const buildTitleLabel = (primaryLabel = "", secondaryLabel = "") => {
    if (!primaryLabel) {
      return secondaryLabel || fallbackLabel;
    }

    if (!secondaryLabel || primaryLabel.localeCompare(secondaryLabel, undefined, { sensitivity: "base" }) === 0) {
      return primaryLabel;
    }

    return `${primaryLabel} · ${secondaryLabel}`;
  };

  const buildPresenceTitleLabel = (mainLabel = "") => {
    const presenceLabel = mainLabel ? `Vous êtes ici · ${mainLabel}` : "Vous êtes ici";
    return buildTitleLabel(presenceLabel, normalizedAddress);
  };

  if (normalizedBadgeRole === "origin") {
    const mainLabel = normalizedLabel || normalizedClientName || normalizedAddress || fallbackLabel;
    return {
      badgeToneClassName: "route-map-stop-index__badge--origin",
      eyebrowLabel: "Vous êtes ici",
      showIcon: false,
      visualLabel: mainLabel,
      titleLabel: buildPresenceTitleLabel(mainLabel),
    };
  }

  if (normalizedBadgeRole === "destination") {
    const visualLabel = normalizedClientName || normalizedLabel || normalizedAddress || fallbackLabel;
    return {
      badgeToneClassName: "route-map-stop-index__badge--destination",
      showIcon: false,
      visualLabel,
      titleLabel: buildTitleLabel(normalizedClientName || normalizedLabel || normalizedAddress, normalizedAddress),
    };
  }

  if (normalizedStopType === "loading") {
    return {
      badgeToneClassName: "route-map-stop-index__badge--origin",
      showIcon: false,
      visualLabel: normalizedAddress || normalizedLabel || fallbackLabel,
      titleLabel: buildTitleLabel(normalizedLabel || normalizedAddress, normalizedAddress),
    };
  }

  if (normalizedStopType === "delivery") {
    const visualLabel = normalizedClientName || normalizedLabel || normalizedAddress || fallbackLabel;
    return {
      badgeToneClassName: "route-map-stop-index__badge--destination",
      showIcon: false,
      visualLabel,
      titleLabel: buildTitleLabel(normalizedClientName || normalizedLabel || normalizedAddress, normalizedAddress),
    };
  }

  return {
    showIcon: false,
    visualLabel: normalizedLabel || normalizedAddress || fallbackLabel,
    titleLabel: buildTitleLabel(normalizedLabel || normalizedAddress, normalizedAddress),
  };
}

function buildRouteBadgeIconHtml(stop = null, stopType = "") {
  const badgeContent = buildRouteBadgeIconContent(stop, stopType);
  const badgeToneClassName = typeof badgeContent.badgeToneClassName === "string"
    ? badgeContent.badgeToneClassName.trim()
    : "";
  const eyebrowLabel = typeof badgeContent.eyebrowLabel === "string" ? badgeContent.eyebrowLabel.trim() : "";
  const showIcon = badgeContent.showIcon !== false;
  const visualLabel = badgeContent.visualLabel || getRouteBadgeIconLabel(stopType);
  const titleLabel = badgeContent.titleLabel || visualLabel;
  const badgeModeClassName = showIcon
    ? "route-map-stop-index__badge--with-icon"
    : "route-map-stop-index__badge--textual";
  const escapedEyebrowLabel = eyebrowLabel ? escapeRouteMapHtml(eyebrowLabel) : "";
  const escapedVisualLabel = escapeRouteMapHtml(visualLabel);
  const escapedTitleLabel = escapeRouteMapHtml(titleLabel);
  const iconHtml = showIcon
    ? '<svg viewBox="0 0 24 24" fill="none" role="presentation" focusable="false"><path d="M12 21.5s-6-5.686-6-10.5a6 6 0 1 1 12 0c0 4.814-6 10.5-6 10.5Zm0-8.6a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z" fill="currentColor"/></svg>'
    : '';
  const eyebrowHtml = eyebrowLabel
    ? '<span class="route-map-stop-index__badge-eyebrow">' + escapedEyebrowLabel + '</span>'
    : '';
  return '<span class="route-map-stop-index__badge route-map-stop-index__badge--icon route-map-stop-index__badge--icon-labeled ' + badgeModeClassName + (badgeToneClassName ? ' ' + badgeToneClassName : '') + '" aria-label="' + escapedTitleLabel + '" title="' + escapedTitleLabel + '">' + iconHtml + eyebrowHtml + '<span class="route-map-stop-index__badge-text">' + escapedVisualLabel + '</span></span>';
}

function buildRouteBadgeHtml({ routeLabel = "", showHouse = false, isGroupedStop = false, presentation = "number", stop = null, stopType = "" } = {}) {
  const normalizedRouteLabel = typeof routeLabel === "string" ? routeLabel.trim() : "";

  if (presentation === "icon") {
    return '<span class="route-map-stop-index__cluster route-map-stop-index__cluster--icon">' + buildRouteBadgeIconHtml(stop, stopType) + '</span>';
  }

  if (!normalizedRouteLabel && !showHouse) {
    return "";
  }

  const stopTypeClassName = stopType === "loading" || stopType === "delivery"
    ? ` route-map-stop-index__badge--${stopType}`
    : "";
  const badgeClassName = (isGroupedStop
    ? "route-map-stop-index__badge route-map-stop-index__badge--grouped"
    : "route-map-stop-index__badge") + stopTypeClassName;

  if (showHouse) {
    return normalizedRouteLabel
      ? '<span class="route-map-stop-index__cluster route-map-stop-index__cluster--with-house"><span class="route-map-stop-index__house" aria-hidden="true"></span><span class="' + badgeClassName + '">' + escapeRouteMapHtml(normalizedRouteLabel) + '</span></span>'
      : '<span class="route-map-stop-index__cluster route-map-stop-index__cluster--house-only"><span class="route-map-stop-index__house" aria-hidden="true"></span></span>';
  }

  return normalizedRouteLabel
    ? '<span class="route-map-stop-index__cluster"><span class="' + badgeClassName + '">' + escapeRouteMapHtml(normalizedRouteLabel) + '</span></span>'
    : "";
}

function collectGeometryCoordinates(geometry) {
  if (!geometry || typeof geometry !== "object") {
    return [];
  }

  if (Array.isArray(geometry.coordinates)) {
    return geometry.coordinates;
  }

  if (geometry.type === "Feature" && geometry.geometry && Array.isArray(geometry.geometry.coordinates)) {
    return geometry.geometry.coordinates;
  }

  if (geometry.geometry && Array.isArray(geometry.geometry.coordinates)) {
    return geometry.geometry.coordinates;
  }

  return [];
}

function collectRouteLatLngsFallback(geometry) {
  return collectGeometryCoordinates(geometry)
    .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
    .map((coordinate) => [coordinate[1], coordinate[0]]);
}

function getTripRoutableStops(trip) {
  return getTripRoutableStopsFromData(trip);
}

function collectStopsWithCoordinates(stops) {
  return collectStopsWithCoordinatesFromData(stops);
}

function buildRouteSegmentsFallback(stops) {
  const orderedStops = Array.isArray(stops) ? stops.filter(Boolean) : [];
  if (orderedStops.length < 2) {
    return orderedStops.length ? [{ index: 1, stops: orderedStops }] : [];
  }

  return orderedStops.slice(0, -1).map((stop, index) => ({
    index: index + 1,
    stops: [stop, orderedStops[index + 1]],
  }));
}

function getTripRouteSegments(trip, assignment) {
  return getTripRouteSegmentsFromData(trip, assignment);
}

function collectRouteSegmentLatLngGroups(segmentGeometries) {
  return collectRouteSegmentLatLngGroupsFromData(segmentGeometries);
}

function flattenRouteSegmentLatLngGroups(segmentLatLngGroups) {
  return flattenRouteSegmentLatLngGroupsFromData(segmentLatLngGroups);
}

function collectRouteLatLngs(geometry) {
  return collectRouteLatLngsFromData(geometry);
}

function getAppliedRouteAlternativeItems(trip) {
  const route = trip?.route && typeof trip.route === "object" ? trip.route : null;
  const items = Array.isArray(route?.applied_route_alternatives) ? route.applied_route_alternatives : [];
  return items.filter((item) => item && typeof item === "object");
}

function getRouteAlternativeDockLatLng(dock) {
  const latitude = Number(dock?.latitude);
  const longitude = Number(dock?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return [latitude, longitude];
}

function normalizeRouteAlternativePreviewItem(routeAlternativePreview) {
  if (!routeAlternativePreview || typeof routeAlternativePreview !== "object") {
    return null;
  }

  return {
    ...routeAlternativePreview,
    previewMode: routeAlternativePreview.mode === "current" ? "current" : "alternative",
  };
}

function getRouteAlternativeOverlayItems(trip, routeAlternativePreview = null) {
  const previewItem = normalizeRouteAlternativePreviewItem(routeAlternativePreview);
  if (previewItem) {
    return [previewItem];
  }

  return getAppliedRouteAlternativeItems(trip).map((item) => ({
    ...item,
    previewMode: "applied",
  }));
}

function getRouteAlternativeSegments(alternative) {
  if (!alternative || typeof alternative !== "object") {
    return [];
  }

  const segments = Array.isArray(alternative.segments)
    ? alternative.segments.filter((segment) => segment && typeof segment === "object")
    : [];
  if (segments.length) {
    return segments;
  }

  return alternative.geometry
    ? [{ geometry: alternative.geometry, kind: "road", label: alternative.label || "Traverse Sorel-Tracy" }]
    : [];
}

function addRouteAlternativeOverlayLayers(mapState, trip, routeAlternativePreview = null) {
  if (!hasMountedMap(mapState) || !window.L) {
    return [];
  }

  const overlayLatLngs = [];
  getRouteAlternativeOverlayItems(trip, routeAlternativePreview).forEach((item) => {
    const previewMode = ["current", "alternative", "applied"].includes(item?.previewMode) ? item.previewMode : "alternative";
    const isCurrentHighlighted = previewMode === "current";
    const shouldDrawCurrent = previewMode !== "applied";
    const currentLatLngs = collectRouteLatLngs(item?.current?.geometry);
    if (shouldDrawCurrent && currentLatLngs.length > 1) {
      const currentLine = window.L.polyline(currentLatLngs, {
        className: "route-map__segment route-map__segment--alternative-current",
        color: isCurrentHighlighted ? "#111827" : "#64748b",
        dashArray: isCurrentHighlighted ? undefined : "5 7",
        lineCap: "round",
        lineJoin: "round",
        opacity: isCurrentHighlighted ? 0.94 : 0.42,
        weight: isCurrentHighlighted ? 6.4 : 4.5,
      }).addTo(mapState.map);
      currentLine.bindTooltip("Actuel via Montréal", {
        className: "route-stop-tooltip",
        direction: "top",
        opacity: 1,
      });
      mapState.routeLayers.push(currentLine);
      overlayLatLngs.push(...currentLatLngs);
    }

    const alternative = item?.alternative && typeof item.alternative === "object" ? item.alternative : {};
    const isAlternativeHighlighted = previewMode !== "current";
    const alternativeSegments = getRouteAlternativeSegments(alternative);
    alternativeSegments.forEach((segment) => {
      const segmentLatLngs = collectRouteLatLngs(segment?.geometry);
      if (segmentLatLngs.length < 2) {
        return;
      }

      const isFerry = segment?.kind === "ferry";
      const line = window.L.polyline(segmentLatLngs, {
        className: isFerry
          ? "route-map__segment route-map__segment--ferry"
          : "route-map__segment route-map__segment--alternative-road",
        color: isFerry ? "#0f766e" : "#2563eb",
        dashArray: isFerry ? "2 8" : (isAlternativeHighlighted ? undefined : "6 7"),
        lineCap: "round",
        lineJoin: "round",
        opacity: isAlternativeHighlighted ? (isFerry ? 1 : 0.88) : 0.28,
        weight: isAlternativeHighlighted ? (isFerry ? 7.8 : 5.8) : 4.2,
      }).addTo(mapState.map);
      line.bindTooltip(segment?.label || (isFerry ? "Traverse Sorel-Tracy" : "Alternative Sorel-Tracy"), {
        className: "route-stop-tooltip",
        direction: "top",
        opacity: 1,
      });
      mapState.routeLayers.push(line);
      overlayLatLngs.push(...segmentLatLngs);
    });

    const docks = Array.isArray(alternative.docks) ? alternative.docks : [];
    docks.forEach((dock) => {
      const dockLatLng = getRouteAlternativeDockLatLng(dock);
      if (!dockLatLng) {
        return;
      }
      const marker = window.L.circleMarker(dockLatLng, {
        className: "route-map__dock-marker",
        color: "#0f766e",
        fillColor: "#ccfbf1",
        fillOpacity: isAlternativeHighlighted ? 0.96 : 0.42,
        opacity: isAlternativeHighlighted ? 1 : 0.5,
        radius: isAlternativeHighlighted ? 6.5 : 5.4,
        weight: isAlternativeHighlighted ? 2.5 : 1.8,
      }).addTo(mapState.map);
      marker.bindTooltip(dock?.label || "Quai traverse", {
        className: "route-stop-tooltip",
        direction: "top",
        opacity: 1,
      });
      mapState.routeLayers.push(marker);
      overlayLatLngs.push(dockLatLng);
    });
  });
  return overlayLatLngs;
}

function buildExactRoutePolylineSegments(segmentLatLngGroups, routeSegments) {
  return buildExactRoutePolylineSegmentsFromData(segmentLatLngGroups, routeSegments);
}

function buildRoutePolylineSegments(routeLatLngs, routeSegments) {
  return buildRoutePolylineSegmentsFromData(routeLatLngs, routeSegments);
}

export function applyAspiratorRouteSegmentColors(routePolylineSegments = [], aspiratorRouteSegmentIndexes = []) {
  const resolvedSegments = Array.isArray(routePolylineSegments) ? routePolylineSegments : [];
  const aspiratorIndexSet = new Set(
    (Array.isArray(aspiratorRouteSegmentIndexes) ? aspiratorRouteSegmentIndexes : [])
      .map((routeSegmentIndex) => Number.parseInt(routeSegmentIndex, 10))
      .filter((routeSegmentIndex) => Number.isInteger(routeSegmentIndex) && routeSegmentIndex > 0),
  );
  if (!aspiratorIndexSet.size) {
    return resolvedSegments;
  }

  return resolvedSegments.map((segment) => {
    const routeSegmentIndex = Number.parseInt(segment?.routeSegmentIndex ?? segment?.index, 10);
    return aspiratorIndexSet.has(routeSegmentIndex)
      ? { ...segment, color: ASPIRATOR_ROUTE_COLOR }
      : segment;
  });
}

function getClientSelectionId(target) {
  return getClientSelectionIdFromFleet(target) || null;
}

function rememberMapBaseView(mapState, view = null) {
  if (!mapState?.map) {
    return;
  }

  if (view && view.mode === "bounds" && Array.isArray(view.bounds) && view.bounds.length) {
    mapState.baseView = {
      mode: "bounds",
      bounds: view.bounds.map((point) => [point[0], point[1]]),
      padding: Array.isArray(view.padding) ? [...view.padding] : [...DEFAULT_PADDING],
    };
    return;
  }

  const center = mapState.map.getCenter();
  mapState.baseView = {
    mode: "view",
    center: [center.lat, center.lng],
    zoom: mapState.map.getZoom(),
  };
}

function focusMapOnStopLatLngs(mapState, stopLatLngs, { padding = STOPS_ONLY_PADDING } = {}) {
  if (!Array.isArray(stopLatLngs) || stopLatLngs.length === 0) {
    mapState.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    rememberMapBaseView(mapState);
    return;
  }

  if (stopLatLngs.length === 1) {
    const center = stopLatLngs[0];
    mapState.map.setView(center, SINGLE_STOP_FOCUS_ZOOM);
    rememberMapBaseView(mapState, {
      mode: "view",
      center: [center[0], center[1]],
      zoom: SINGLE_STOP_FOCUS_ZOOM,
    });
    return;
  }

  mapState.map.fitBounds(window.L.latLngBounds(stopLatLngs), { padding });
  rememberMapBaseView(mapState, { mode: "bounds", bounds: stopLatLngs, padding });
}

function restoreMapBaseView(mapState) {
  if (!hasMountedMap(mapState) || !mapState.baseView) {
    return false;
  }

  if (mapState.baseView.mode === "bounds" && Array.isArray(mapState.baseView.bounds) && mapState.baseView.bounds.length) {
    if (!window.L?.latLngBounds) {
      return false;
    }

    const boundsOptions = {
      animate: false,
      padding: Array.isArray(mapState.baseView.padding) ? mapState.baseView.padding : DEFAULT_PADDING,
    };
    if (mapState.baseView.bounds.length === 1) {
      boundsOptions.maxZoom = SINGLE_STOP_FOCUS_ZOOM;
    }

    mapState.map.fitBounds(window.L.latLngBounds(mapState.baseView.bounds), boundsOptions);
    return true;
  }

  if (Array.isArray(mapState.baseView.center) && mapState.baseView.center.length === 2) {
    mapState.map.setView(mapState.baseView.center, mapState.baseView.zoom, { animate: false });
    return true;
  }

  return false;
}

function hasMountedMap(mapState) {
  return Boolean(mapState?.map) && mapState.isDisposed !== true;
}

function hasMountedLayer(mapState, layer) {
  return hasMountedMap(mapState)
    && Boolean(layer)
    && mapState.map.hasLayer?.(layer) === true;
}

function hasTrackedBadgeMarker(mapState, marker) {
  return hasMountedMap(mapState)
    && Boolean(marker)
    && (mapState.stopBadgeLayer?.hasLayer?.(marker) === true || mapState.map.hasLayer?.(marker) === true);
}

function escapeRouteMapHtml(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function closeClusterPreviewTooltip(mapState) {
  if (!mapState?.clusterPreviewTooltip) {
    return;
  }

  mapState.clusterPreviewTooltip.remove?.();
  mapState.clusterPreviewTooltip = null;
}

function openClusterPreviewTooltip(mapState, previewElement) {
  if (!hasMountedMap(mapState) || !isHtmlElement(previewElement) || !window.L?.tooltip) {
    return;
  }

  const tooltipLabel = previewElement.dataset.routePreviewTooltip;
  if (typeof tooltipLabel !== "string" || !tooltipLabel.trim()) {
    closeClusterPreviewTooltip(mapState);
    return;
  }

  const mapContainer = typeof mapState.map.getContainer === "function"
    ? mapState.map.getContainer()
    : null;
  if (!isHtmlElement(mapContainer)) {
    return;
  }

  const previewBounds = previewElement.getBoundingClientRect();
  const containerBounds = mapContainer.getBoundingClientRect();
  const containerPoint = [
    (previewBounds.left - containerBounds.left) + (previewBounds.width / 2),
    (previewBounds.top - containerBounds.top) + (previewBounds.height / 2),
  ];
  const tooltip = mapState.clusterPreviewTooltip || window.L.tooltip({
    className: "route-stop-tooltip",
    direction: "top",
    offset: [0, -10],
    opacity: 1,
  });

  tooltip
    .setContent(tooltipLabel)
    .setLatLng(mapState.map.containerPointToLatLng(containerPoint));

  if (mapState.map.hasLayer?.(tooltip) !== true) {
    tooltip.addTo(mapState.map);
  }

  mapState.clusterPreviewTooltip = tooltip;
}

function resolveBadgeMarkerElement(marker) {
  return marker?.getElement?.() || marker?._icon || null;
}

function resolveBadgeContentElement(marker) {
  const markerElement = resolveBadgeMarkerElement(marker);
  if (!isHtmlElement(markerElement)) {
    return null;
  }

  if (markerElement.classList.contains("route-map-stop-index__badge--icon-labeled")) {
    return markerElement;
  }

  return typeof markerElement.querySelector === "function"
    ? markerElement.querySelector(".route-map-stop-index__badge--icon-labeled")
    : null;
}

function clampNumber(value, min, max) {
  if (!Number.isFinite(value)) {
    return min;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
    return value;
  }

  return Math.min(max, Math.max(min, value));
}

const PILL_COLLISION_MARGIN_PX = 4;

function normalizeRouteMapPoint(point) {
  if (Array.isArray(point) && point.length >= 2) {
    const x = Number(point[0]);
    const y = Number(point[1]);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }

  const x = Number(point?.x);
  const y = Number(point?.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function normalizeRouteMapSize(size) {
  const width = Number(size?.width);
  const height = Number(size?.height);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }

  return { width, height };
}

function buildRouteMapRect(left, top, width, height) {
  return {
    bottom: top + height,
    height,
    left,
    right: left + width,
    top,
    width,
  };
}

function normalizeRouteMapRect(rect) {
  const left = Number(rect?.left);
  const top = Number(rect?.top);
  const width = Number.isFinite(Number(rect?.width))
    ? Number(rect.width)
    : Number(rect?.right) - left;
  const height = Number.isFinite(Number(rect?.height))
    ? Number(rect.height)
    : Number(rect?.bottom) - top;

  if (!Number.isFinite(left) || !Number.isFinite(top) || !Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }

  return buildRouteMapRect(left, top, width, height);
}

function clampRouteMapRectToBounds(rect, mapSize, edgeInset = 0) {
  if (!rect || !mapSize) {
    return rect;
  }

  const maxLeft = Math.max(edgeInset, mapSize.width - rect.width - edgeInset);
  const maxTop = Math.max(edgeInset, mapSize.height - rect.height - edgeInset);
  const left = clampNumber(rect.left, edgeInset, maxLeft);
  const top = clampNumber(rect.top, edgeInset, maxTop);
  return buildRouteMapRect(left, top, rect.width, rect.height);
}

function computeRouteMapRectOverflow(rect, mapSize, edgeInset = 0) {
  if (!rect || !mapSize) {
    return 0;
  }

  return Math.max(edgeInset - rect.left, 0)
    + Math.max(edgeInset - rect.top, 0)
    + Math.max(rect.right - (mapSize.width - edgeInset), 0)
    + Math.max(rect.bottom - (mapSize.height - edgeInset), 0);
}

function doRouteMapRectsOverlap(leftRect, rightRect, margin = 0) {
  if (!leftRect || !rightRect) {
    return false;
  }

  return leftRect.right > rightRect.left - margin
    && leftRect.left < rightRect.right + margin
    && leftRect.bottom > rightRect.top - margin
    && leftRect.top < rightRect.bottom + margin;
}

function computeRouteMapRectOverlapArea(leftRect, rightRect, margin = 0) {
  if (!doRouteMapRectsOverlap(leftRect, rightRect, margin)) {
    return 0;
  }

  const left = Math.max(leftRect.left, rightRect.left - margin);
  const right = Math.min(leftRect.right, rightRect.right + margin);
  const top = Math.max(leftRect.top, rightRect.top - margin);
  const bottom = Math.min(leftRect.bottom, rightRect.bottom + margin);
  return Math.max(0, right - left) * Math.max(0, bottom - top);
}

function computeRouteMapRectDistance(leftRect, rightRect) {
  if (!leftRect || !rightRect || doRouteMapRectsOverlap(leftRect, rightRect)) {
    return 0;
  }

  const dx = Math.max(rightRect.left - leftRect.right, leftRect.left - rightRect.right, 0);
  const dy = Math.max(rightRect.top - leftRect.bottom, leftRect.top - rightRect.bottom, 0);
  return Math.hypot(dx, dy);
}

function computePointToRouteMapSegmentDistance(point, segmentStart, segmentEnd) {
  const segmentX = segmentEnd.x - segmentStart.x;
  const segmentY = segmentEnd.y - segmentStart.y;
  const lengthSquared = (segmentX * segmentX) + (segmentY * segmentY);

  if (lengthSquared <= 1e-6) {
    return Math.hypot(point.x - segmentStart.x, point.y - segmentStart.y);
  }

  const t = clampNumber(
    (((point.x - segmentStart.x) * segmentX) + ((point.y - segmentStart.y) * segmentY)) / lengthSquared,
    0,
    1,
  );
  return Math.hypot(point.x - (segmentStart.x + (segmentX * t)), point.y - (segmentStart.y + (segmentY * t)));
}

function computePointToRouteMapRectDistance(point, rect) {
  const dx = Math.max(rect.left - point.x, point.x - rect.right, 0);
  const dy = Math.max(rect.top - point.y, point.y - rect.bottom, 0);
  return Math.hypot(dx, dy);
}

function routeMapRectContainsPoint(rect, point) {
  return Boolean(rect && point)
    && point.x >= rect.left
    && point.x <= rect.right
    && point.y >= rect.top
    && point.y <= rect.bottom;
}

function routeMapOrientation(pointA, pointB, pointC) {
  const value = ((pointB.y - pointA.y) * (pointC.x - pointB.x)) - ((pointB.x - pointA.x) * (pointC.y - pointB.y));
  if (Math.abs(value) < 1e-6) {
    return 0;
  }

  return value > 0 ? 1 : 2;
}

function routeMapPointOnSegment(pointA, pointB, pointC) {
  return pointB.x <= Math.max(pointA.x, pointC.x) + 1e-6
    && pointB.x >= Math.min(pointA.x, pointC.x) - 1e-6
    && pointB.y <= Math.max(pointA.y, pointC.y) + 1e-6
    && pointB.y >= Math.min(pointA.y, pointC.y) - 1e-6;
}

function doRouteMapSegmentsIntersect(segmentAStart, segmentAEnd, segmentBStart, segmentBEnd) {
  const orientation1 = routeMapOrientation(segmentAStart, segmentAEnd, segmentBStart);
  const orientation2 = routeMapOrientation(segmentAStart, segmentAEnd, segmentBEnd);
  const orientation3 = routeMapOrientation(segmentBStart, segmentBEnd, segmentAStart);
  const orientation4 = routeMapOrientation(segmentBStart, segmentBEnd, segmentAEnd);

  if (orientation1 !== orientation2 && orientation3 !== orientation4) {
    return true;
  }

  return (orientation1 === 0 && routeMapPointOnSegment(segmentAStart, segmentBStart, segmentAEnd))
    || (orientation2 === 0 && routeMapPointOnSegment(segmentAStart, segmentBEnd, segmentAEnd))
    || (orientation3 === 0 && routeMapPointOnSegment(segmentBStart, segmentAStart, segmentBEnd))
    || (orientation4 === 0 && routeMapPointOnSegment(segmentBStart, segmentAEnd, segmentBEnd));
}

function doesRouteMapSegmentIntersectRect(segmentStart, segmentEnd, rect) {
  if (routeMapRectContainsPoint(rect, segmentStart) || routeMapRectContainsPoint(rect, segmentEnd)) {
    return true;
  }

  const topLeft = { x: rect.left, y: rect.top };
  const topRight = { x: rect.right, y: rect.top };
  const bottomLeft = { x: rect.left, y: rect.bottom };
  const bottomRight = { x: rect.right, y: rect.bottom };

  return doRouteMapSegmentsIntersect(segmentStart, segmentEnd, topLeft, topRight)
    || doRouteMapSegmentsIntersect(segmentStart, segmentEnd, topRight, bottomRight)
    || doRouteMapSegmentsIntersect(segmentStart, segmentEnd, bottomRight, bottomLeft)
    || doRouteMapSegmentsIntersect(segmentStart, segmentEnd, bottomLeft, topLeft);
}

function computeRouteMapSegmentToRectDistance(segmentStart, segmentEnd, rect) {
  if (doesRouteMapSegmentIntersectRect(segmentStart, segmentEnd, rect)) {
    return 0;
  }

  const corners = [
    { x: rect.left, y: rect.top },
    { x: rect.right, y: rect.top },
    { x: rect.right, y: rect.bottom },
    { x: rect.left, y: rect.bottom },
  ];
  const endpointDistance = Math.min(
    computePointToRouteMapRectDistance(segmentStart, rect),
    computePointToRouteMapRectDistance(segmentEnd, rect),
  );
  const cornerDistance = Math.min(...corners.map((corner) => computePointToRouteMapSegmentDistance(corner, segmentStart, segmentEnd)));
  return Math.min(endpointDistance, cornerDistance);
}

function normalizeRouteMapPointGroups(routePointGroups = []) {
  return (Array.isArray(routePointGroups) ? routePointGroups : [])
    .map((group) => (Array.isArray(group) ? group : [])
      .map(normalizeRouteMapPoint)
      .filter(Boolean))
    .filter((group) => group.length >= 2);
}

function computeIconBadgeRoutePenalty(rect, routePointGroups = []) {
  const pointGroups = normalizeRouteMapPointGroups(routePointGroups);
  if (!rect || !pointGroups.length) {
    return 0;
  }

  let penalty = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  pointGroups.forEach((group) => {
    for (let index = 0; index < group.length - 1; index += 1) {
      const distance = computeRouteMapSegmentToRectDistance(group[index], group[index + 1], rect);
      nearestDistance = Math.min(nearestDistance, distance);
      if (distance <= 0) {
        penalty += 900;
      }
    }
  });

  if (Number.isFinite(nearestDistance) && nearestDistance < ICON_BADGE_ROUTE_CLEARANCE_PX) {
    penalty += (ICON_BADGE_ROUTE_CLEARANCE_PX - nearestDistance) * 14;
  }

  return penalty;
}

function computeIconBadgeObstaclePenalty(rect, obstacleRects = []) {
  return (Array.isArray(obstacleRects) ? obstacleRects : [])
    .map(normalizeRouteMapRect)
    .filter(Boolean)
    .reduce((penalty, obstacleRect) => {
      if (doRouteMapRectsOverlap(rect, obstacleRect, ICON_BADGE_OBSTACLE_MARGIN_PX)) {
        return penalty + 620 + (computeRouteMapRectOverlapArea(rect, obstacleRect, ICON_BADGE_OBSTACLE_MARGIN_PX) * 1.8);
      }

      const distance = computeRouteMapRectDistance(rect, obstacleRect);
      return distance < ICON_BADGE_OBSTACLE_MARGIN_PX
        ? penalty + ((ICON_BADGE_OBSTACLE_MARGIN_PX - distance) * 24)
        : penalty;
    }, 0);
}

function buildIconBadgeCandidateRect(placement, currentPoint, badgeSize, gap) {
  switch (placement) {
    case "bottom":
      return buildRouteMapRect(currentPoint.x - (badgeSize.width / 2), currentPoint.y + gap, badgeSize.width, badgeSize.height);
    case "left":
      return buildRouteMapRect(currentPoint.x - badgeSize.width - gap, currentPoint.y - (badgeSize.height / 2), badgeSize.width, badgeSize.height);
    case "right":
      return buildRouteMapRect(currentPoint.x + gap, currentPoint.y - (badgeSize.height / 2), badgeSize.width, badgeSize.height);
    case "top-left":
      return buildRouteMapRect(currentPoint.x - badgeSize.width - gap, currentPoint.y - badgeSize.height - gap, badgeSize.width, badgeSize.height);
    case "top-right":
      return buildRouteMapRect(currentPoint.x + gap, currentPoint.y - badgeSize.height - gap, badgeSize.width, badgeSize.height);
    case "bottom-left":
      return buildRouteMapRect(currentPoint.x - badgeSize.width - gap, currentPoint.y + gap, badgeSize.width, badgeSize.height);
    case "bottom-right":
      return buildRouteMapRect(currentPoint.x + gap, currentPoint.y + gap, badgeSize.width, badgeSize.height);
    case "top":
    default:
      return buildRouteMapRect(currentPoint.x - (badgeSize.width / 2), currentPoint.y - badgeSize.height - gap, badgeSize.width, badgeSize.height);
  }
}

function computeIconBadgeDirectionPenalty(rect, currentPoint, neighborPoint) {
  const neighbor = normalizeRouteMapPoint(neighborPoint);
  if (!rect || !currentPoint || !neighbor) {
    return 0;
  }

  const routeVector = {
    x: neighbor.x - currentPoint.x,
    y: neighbor.y - currentPoint.y,
  };
  const routeLength = Math.hypot(routeVector.x, routeVector.y);
  if (routeLength < 1e-6) {
    return 0;
  }

  const badgeVector = {
    x: (rect.left + (rect.width / 2)) - currentPoint.x,
    y: (rect.top + (rect.height / 2)) - currentPoint.y,
  };
  const badgeLength = Math.hypot(badgeVector.x, badgeVector.y);
  if (badgeLength < 1e-6) {
    return 0;
  }

  const dot = ((routeVector.x / routeLength) * (badgeVector.x / badgeLength))
    + ((routeVector.y / routeLength) * (badgeVector.y / badgeLength));
  return Math.max(0, dot + 1) * 72;
}

function getIconBadgePlacementBias(placement) {
  switch (placement) {
    case "top":
    case "bottom":
      return 0;
    case "left":
    case "right":
      return 8;
    default:
      return 16;
  }
}

export function resolveIconBadgePlacement({
  badgeSize: rawBadgeSize = null,
  currentPoint: rawCurrentPoint = null,
  edgeInset = MOBILE_ICON_BADGE_EDGE_INSET,
  gap = MOBILE_ICON_BADGE_POINT_GAP,
  mapSize: rawMapSize = null,
  neighborPoint: rawNeighborPoint = null,
  obstacleRects = [],
  routePointGroups = [],
} = {}) {
  const currentPoint = normalizeRouteMapPoint(rawCurrentPoint);
  const badgeSize = normalizeRouteMapSize(rawBadgeSize) || { width: ICON_TEXT_BADGE_WIDTH, height: ICON_TEXT_BADGE_HEIGHT };
  const mapSize = normalizeRouteMapSize(rawMapSize);

  if (!currentPoint) {
    const fallbackRect = buildRouteMapRect(0, 0, badgeSize.width, badgeSize.height);
    return {
      placement: "top",
      left: fallbackRect.left,
      top: fallbackRect.top,
      rect: fallbackRect,
      score: 0,
    };
  }

  const normalizedNeighborPoint = normalizeRouteMapPoint(rawNeighborPoint);
  const normalizedRoutePointGroups = normalizeRouteMapPointGroups(routePointGroups);
  if (normalizedNeighborPoint && !normalizedRoutePointGroups.length) {
    normalizedRoutePointGroups.push([currentPoint, normalizedNeighborPoint]);
  }

  let bestPlacement = null;

  ICON_BADGE_PLACEMENT_CANDIDATES.forEach((placement, index) => {
    const rawRect = buildIconBadgeCandidateRect(placement, currentPoint, badgeSize, gap);
    const overflowScore = computeRouteMapRectOverflow(rawRect, mapSize, edgeInset) * 1000;
    const rect = clampRouteMapRectToBounds(rawRect, mapSize, edgeInset);
    const routeScore = computeIconBadgeRoutePenalty(rect, normalizedRoutePointGroups);
    const directionScore = computeIconBadgeDirectionPenalty(rect, currentPoint, normalizedNeighborPoint);
    const obstacleScore = computeIconBadgeObstaclePenalty(rect, obstacleRects);
    const score = overflowScore
      + routeScore
      + directionScore
      + obstacleScore
      + getIconBadgePlacementBias(placement)
      + (index * 0.01);

    if (!bestPlacement || score < bestPlacement.score) {
      bestPlacement = {
        directionScore,
        left: rect.left,
        obstacleScore,
        overflowScore,
        placement,
        rect,
        routeScore,
        score,
        top: rect.top,
      };
    }
  });

  return bestPlacement;
}

function resolveBadgeNeighborPoint(mapState, marker) {
  if (!hasMountedMap(mapState) || !marker) {
    return null;
  }

  const nextLatLng = marker.__nextStopLatLng ? window.L.latLng(marker.__nextStopLatLng[0], marker.__nextStopLatLng[1]) : null;
  const previousLatLng = marker.__previousStopLatLng ? window.L.latLng(marker.__previousStopLatLng[0], marker.__previousStopLatLng[1]) : null;

  if (nextLatLng) {
    return mapState.map.latLngToContainerPoint(nextLatLng);
  }

  if (previousLatLng) {
    return mapState.map.latLngToContainerPoint(previousLatLng);
  }

  return null;
}

function collectRoutePointGroupsForBadgeLayout(mapState) {
  if (!hasMountedMap(mapState) || !Array.isArray(mapState.routeSegments)) {
    return [];
  }

  return mapState.routeSegments
    .map((segment) => (Array.isArray(segment?.baseLatLngs) ? segment.baseLatLngs : [])
      .map((latlng) => {
        if (!Array.isArray(latlng) || latlng.length < 2) {
          return null;
        }

        return mapState.map.latLngToContainerPoint(window.L.latLng(latlng[0], latlng[1]));
      })
      .filter(Boolean))
    .filter((group) => group.length >= 2);
}

function getMapRelativeElementRect(element, mapRect) {
  if (!isHtmlElement(element) || !mapRect) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return normalizeRouteMapRect({
    height: rect.height,
    left: rect.left - mapRect.left,
    top: rect.top - mapRect.top,
    width: rect.width,
  });
}

function resolveRouteMapPillOverlaps(mapState, mapContainer, mapRect) {
  if (!mapContainer || !mapRect) {
    return;
  }

  const distancePill = mapContainer.querySelector(".route-map-route-distance__pill");
  const pillEntries = [];

  if (isHtmlElement(distancePill)) {
    pillEntries.push({
      element: distancePill,
      isDistancePill: true,
      priority: 0,
    });
  }

  if (Array.isArray(mapState.badgeMarkers)) {
    mapState.badgeMarkers.forEach((marker) => {
      if (!hasTrackedBadgeMarker(mapState, marker)) {
        return;
      }

      const badgeElement = resolveBadgeContentElement(marker);
      if (!isHtmlElement(badgeElement) || typeof badgeElement.style !== "object") {
        return;
      }

      let priority = 3;
      if (badgeElement.classList.contains("route-map-stop-index__badge--origin")) {
        priority = 1;
      } else if (badgeElement.classList.contains("route-map-stop-index__badge--destination")) {
        priority = 2;
      }

      pillEntries.push({
        element: badgeElement,
        isDistancePill: false,
        priority,
      });
    });
  }

  if (pillEntries.length < 2) {
    return;
  }

  // Compute initial rects relative to the map container
  pillEntries.forEach((entry) => {
    const rect = entry.element.getBoundingClientRect();
    entry.rect = {
      bottom: rect.bottom - mapRect.top,
      left: rect.left - mapRect.left,
      right: rect.right - mapRect.left,
      top: rect.top - mapRect.top,
    };
  });

  function rectsOverlap(rectA, rectB, margin) {
    if (!rectA || !rectB) {
      return false;
    }

    const overlapLeft = Math.max(rectA.left, rectB.left);
    const overlapTop = Math.max(rectA.top, rectB.top);
    const overlapRight = Math.min(rectA.right, rectB.right);
    const overlapBottom = Math.min(rectA.bottom, rectB.bottom);

    return (overlapRight - overlapLeft) > margin && (overlapBottom - overlapTop) > margin;
  }

  function pushRectAway(subject, anchor, margin) {
    const pushLeft = anchor.left - subject.right - margin;
    const pushRight = anchor.right - subject.left + margin;
    const pushUp = anchor.top - subject.bottom - margin;
    const pushDown = anchor.bottom - subject.top + margin;

    const absLeft = Math.abs(pushLeft);
    const absRight = Math.abs(pushRight);
    const absUp = Math.abs(pushUp);
    const absDown = Math.abs(pushDown);

    const minPush = Math.min(absLeft, absRight, absUp, absDown);

    if (minPush === absLeft) {
      subject.left += pushLeft;
      subject.right += pushLeft;
    } else if (minPush === absRight) {
      subject.left += pushRight;
      subject.right += pushRight;
    } else if (minPush === absUp) {
      subject.top += pushUp;
      subject.bottom += pushUp;
    } else {
      subject.top += pushDown;
      subject.bottom += pushDown;
    }
  }

  // Iterative collision resolution
  const MAX_ITERATIONS = 5;
  for (let iter = 0; iter < MAX_ITERATIONS; iter += 1) {
    let hasOverlap = false;

    for (let i = 0; i < pillEntries.length; i += 1) {
      for (let j = i + 1; j < pillEntries.length; j += 1) {
        if (!rectsOverlap(pillEntries[i].rect, pillEntries[j].rect, PILL_COLLISION_MARGIN_PX)) {
          continue;
        }

        hasOverlap = true;
        const higher = pillEntries[i].priority <= pillEntries[j].priority ? pillEntries[i] : pillEntries[j];
        const lower = higher === pillEntries[i] ? pillEntries[j] : pillEntries[i];

        pushRectAway(lower.rect, higher.rect, PILL_COLLISION_MARGIN_PX);
      }
    }

    if (!hasOverlap) {
      break;
    }
  }

  // Apply accumulated translations to badge elements
  pillEntries.forEach((entry) => {
    if (entry.isDistancePill) {
      return;
    }

    const element = entry.element;
    if (!isHtmlElement(element) || typeof element.style !== "object") {
      return;
    }

    const elRect = element.getBoundingClientRect();
    const currentLeft = elRect.left - mapRect.left;
    const currentTop = elRect.top - mapRect.top;
    const shiftX = Math.round(entry.rect.left - currentLeft);
    const shiftY = Math.round(entry.rect.top - currentTop);

    if (shiftX === 0 && shiftY === 0) {
      return;
    }

    const currentTranslate = element.style.translate || "0px 0px";
    const translateParts = String(currentTranslate).split(/\s+/);
    const accumulatedX = (Number.parseInt(translateParts[0], 10) || 0) + shiftX;
    const accumulatedY = (Number.parseInt(translateParts[1], 10) || 0) + shiftY;
    element.style.translate = `${accumulatedX}px ${accumulatedY}px`;
  });
}

function refreshRouteBadgePositions(mapState) {
  if (!hasMountedMap(mapState) || !Array.isArray(mapState.badgeMarkers) || !mapState.badgeMarkers.length) {
    return;
  }

  const mapContainer = typeof mapState.map.getContainer === "function"
    ? mapState.map.getContainer()
    : null;
  if (!isHtmlElement(mapContainer)) {
    return;
  }

  const mapRect = mapContainer.getBoundingClientRect();
  const isIconPresentation = mapState.stopBadgePresentation === "icon";
  const mapSize = { width: mapRect.width, height: mapRect.height };
  const routePointGroups = isIconPresentation ? collectRoutePointGroupsForBadgeLayout(mapState) : [];
  const distancePillRect = isIconPresentation
    ? getMapRelativeElementRect(mapContainer.querySelector(".route-map-route-distance__pill"), mapRect)
    : null;
  const placedBadgeRects = [];

  mapState.badgeMarkers.forEach((marker) => {
    if (!hasTrackedBadgeMarker(mapState, marker)) {
      return;
    }

    const badgeElement = resolveBadgeContentElement(marker);
    if (!isHtmlElement(badgeElement) || typeof badgeElement.style !== "object") {
      return;
    }

    badgeElement.style.translate = "";

    if (!isIconPresentation) {
      return;
    }

    const markerLatLng = marker.getLatLng?.();
    if (!markerLatLng) {
      return;
    }

    const currentPoint = mapState.map.latLngToContainerPoint(markerLatLng);
    const neighborPoint = resolveBadgeNeighborPoint(mapState, marker);
    const badgeRect = badgeElement.getBoundingClientRect();
    const currentLeft = badgeRect.left - mapRect.left;
    const currentTop = badgeRect.top - mapRect.top;
    const placement = resolveIconBadgePlacement({
      badgeSize: {
        height: badgeRect.height,
        width: badgeRect.width,
      },
      currentPoint,
      mapSize,
      neighborPoint,
      obstacleRects: [
        distancePillRect,
        ...placedBadgeRects,
      ].filter(Boolean),
      routePointGroups,
    });
    const shiftX = Math.round(placement.left - currentLeft);
    const shiftY = Math.round(placement.top - currentTop);

    badgeElement.style.translate = `${shiftX}px ${shiftY}px`;
    badgeElement.dataset.routeBadgePlacement = placement.placement;
    placedBadgeRects.push(placement.rect);
  });

  resolveRouteMapPillOverlaps(mapState, mapContainer, mapRect);
}

function scheduleRouteBadgeRefresh(mapState) {
  if (!hasMountedMap(mapState)) {
    return;
  }

  const hostWindow = resolveMapHostWindow(mapState);
  if (!hostWindow?.requestAnimationFrame) {
    refreshRouteBadgePositions(mapState);
    return;
  }

  if (mapState.badgeLayoutFrameId) {
    hostWindow.cancelAnimationFrame(mapState.badgeLayoutFrameId);
  }

  mapState.badgeLayoutFrameId = hostWindow.requestAnimationFrame(() => {
    mapState.badgeLayoutFrameId = 0;
    refreshRouteBadgePositions(mapState);
  });
}

function buildStopBadgeClusterPreviewOffsets(count) {
  if (!Number.isInteger(count) || count <= 0) {
    return [];
  }

  // Keep enough room for the 18px badges in the most crowded groups.
  const radius = count > 6 ? 24 : STOP_BADGE_CLUSTER_PREVIEW_RADIUS;
  return Array.from({ length: count }, (_, index) => {
    const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / count);
    return {
      x: Math.round(Math.cos(angle) * radius),
      y: Math.round(Math.sin(angle) * radius),
    };
  });
}

function getStopBadgeClusterChildMarkers(cluster) {
  return Array.isArray(cluster?.getAllChildMarkers?.())
    ? cluster.getAllChildMarkers()
    : [];
}

function getStopBadgeClusterRouteNumbers(childMarkers) {
  return Array.from(new Set(
    (Array.isArray(childMarkers) ? childMarkers : [])
      .map((marker) => marker?.__routeNumber)
      .filter((value) => Number.isInteger(value)),
  )).sort((left, right) => left - right);
}

function getStopBadgeClusterPreviewEntries(childMarkers) {
  const previewEntries = [];
  const seenRouteNumbers = new Set();

  (Array.isArray(childMarkers) ? childMarkers : []).forEach((marker) => {
    const routeNumber = marker?.__routeNumber;
    if (!Number.isInteger(routeNumber) || seenRouteNumbers.has(routeNumber)) {
      return;
    }

    seenRouteNumbers.add(routeNumber);
    const routeLabel = buildStopBadgeClusterRouteLabel(marker);
    previewEntries.push({
      routeLabel,
      routeNumber,
      stopType: marker?.__stopType || "",
      tooltipLabel: typeof marker?.__tooltipLabel === "string" && marker.__tooltipLabel.trim()
        ? marker.__tooltipLabel.trim()
        : (routeLabel ? `Route ${routeLabel}` : `Route ${routeNumber}`),
    });
  });

  return previewEntries.sort((left, right) => left.routeNumber - right.routeNumber);
}

function hasStopBadgeClusterHouseMarker(childMarkers) {
  return (Array.isArray(childMarkers) ? childMarkers : []).some((marker) => marker?.__stopType === "depot_start");
}

function buildStopBadgeClusterPreviewItems(previewEntries) {
  const normalizedPreviewEntries = Array.isArray(previewEntries) ? previewEntries : [];

  if (!normalizedPreviewEntries.length) {
    return [];
  }

  const previewEntriesToRender = normalizedPreviewEntries.length > STOP_BADGE_CLUSTER_PREVIEW_MAX
    ? [
        ...normalizedPreviewEntries.slice(0, STOP_BADGE_CLUSTER_PREVIEW_MAX - 1),
        {
          isOverflow: true,
          routeNumber: null,
          tooltipLabel: `${normalizedPreviewEntries.length - (STOP_BADGE_CLUSTER_PREVIEW_MAX - 1)} autre(s) étape(s) dans ce groupe`,
        },
      ]
    : normalizedPreviewEntries;
  const offsets = buildStopBadgeClusterPreviewOffsets(previewEntriesToRender.length);

  return previewEntriesToRender.map((entry, index) => ({
    isOverflow: entry.isOverflow === true,
    stopType: entry.stopType || "",
    label: entry.isOverflow === true ? "+" : (entry.routeLabel || String(entry.routeNumber)),
    tooltipLabel: entry.tooltipLabel,
    x: offsets[index]?.x ?? 0,
    y: offsets[index]?.y ?? 0,
  }));
}

function createSingleStopBadgeClusterIcon({ hasHouse = false, routeNumber = null, routeLabel = "", isGroupedStop = false, presentation = "number", stopType = "" } = {}) {
  if (!window.L?.divIcon) {
    return null;
  }

  const normalizedRouteLabel = typeof routeLabel === "string" && routeLabel.trim()
    ? routeLabel.trim()
    : (Number.isInteger(routeNumber) ? String(routeNumber) : "");
  const badgeHtml = buildRouteBadgeHtml({
    routeLabel: normalizedRouteLabel,
    showHouse: hasHouse,
    isGroupedStop,
    presentation,
    stopType,
  });

  if (!badgeHtml) {
    return null;
  }

  const iconAnchor = hasHouse
    ? normalizedRouteLabel
      ? [54, 30]
      : [23, 60]
    : [0, 8];
  const iconSize = hasHouse
    ? normalizedRouteLabel
      ? [78, 60]
      : [49, 60]
    : [normalizedRouteLabel.length > 2 ? 36 : 18, 18];

  return window.L.divIcon({
    className: `route-map-stop-cluster-icon ${buildRouteBadgeClassName(routeNumber)}`,
    html: badgeHtml,
    iconSize,
    iconAnchor,
  });
}

function createStopBadgeClusterIcon(cluster, presentation = "number") {
  if (!cluster || !window.L?.divIcon) {
    return null;
  }

  const childMarkers = getStopBadgeClusterChildMarkers(cluster);
  const childCount = Number.isInteger(cluster.getChildCount?.())
    ? cluster.getChildCount()
    : 0;
  const previewEntries = getStopBadgeClusterPreviewEntries(childMarkers);
  const routeNumbers = getStopBadgeClusterRouteNumbers(childMarkers);
  const hasHouse = hasStopBadgeClusterHouseMarker(childMarkers);

  if (routeNumbers.length <= 1) {
    const primaryMarker = childMarkers.find((marker) => Number.isInteger(marker?.__routeNumber)) || childMarkers[0] || null;
    const singleBadgeIcon = createSingleStopBadgeClusterIcon({
      hasHouse,
      isGroupedStop: primaryMarker?.__isGroupedStop === true,
      routeLabel: buildStopBadgeClusterRouteLabel(primaryMarker),
      routeNumber: routeNumbers[0] ?? null,
      stopType: primaryMarker?.__stopType || "",
      presentation,
    });
    if (singleBadgeIcon) {
      return singleBadgeIcon;
    }
  }

  const previewItems = buildStopBadgeClusterPreviewItems(previewEntries);
  const size = previewItems.length ? STOP_BADGE_CLUSTER_ICON_SIZE : (childCount >= 10 ? 40 : 36);
  const houseHtml = hasHouse
    ? '<span class="route-map-stop-index__house route-map-stop-cluster__house" aria-hidden="true"></span>'
    : "";
  const previewHtml = previewItems
    .map((item) => `<span class="route-map-stop-cluster__preview${item.isOverflow ? ' route-map-stop-cluster__preview--overflow' : item.stopType === "loading" || item.stopType === "delivery" ? ` route-map-stop-cluster__preview--${item.stopType}` : ''}" data-route-preview-tooltip="${escapeRouteMapHtml(item.tooltipLabel)}" style="--cluster-preview-x:${item.x}px;--cluster-preview-y:${item.y}px;">${escapeRouteMapHtml(item.label)}</span>`)
    .join("");

  return window.L.divIcon({
    className: "route-map-stop-cluster-icon",
    html: `<span class="route-map-stop-cluster__shell${hasHouse ? ' route-map-stop-cluster__shell--with-house' : ''}">${houseHtml}${previewHtml}<span class="route-map-stop-cluster__bubble"><span class="route-map-stop-cluster__bubble-dots" aria-hidden="true"><span></span><span></span><span></span></span></span></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function createStopBadgeLayer(map, presentation = "number") {
  if (!map || !window.L) {
    return null;
  }

  if (typeof window.L.markerClusterGroup === "function") {
    const badgeLayer = window.L.markerClusterGroup({
      animate: false,
      disableClusteringAtZoom: STOP_BADGE_CLUSTER_DISABLE_ZOOM,
      maxClusterRadius: STOP_BADGE_CLUSTER_RADIUS,
      showCoverageOnHover: false,
      spiderfyDistanceMultiplier: STOP_BADGE_SPIDERFY_DISTANCE_MULTIPLIER,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: false,
      iconCreateFunction: (cluster) => createStopBadgeClusterIcon(cluster, presentation),
      spiderLegPolylineOptions: {
        weight: 2,
        color: "#111827",
        opacity: 0.4,
      },
    });

    badgeLayer.on("clusterclick", (event) => {
      const cluster = event?.layer;
      if (!cluster || typeof map.getZoom !== "function") {
        return;
      }

      if (map.getZoom() >= STOP_BADGE_CLUSTER_DISABLE_ZOOM - 1 && typeof cluster.spiderfy === "function") {
        cluster.spiderfy();
        return;
      }

      if (typeof cluster.zoomToBounds === "function") {
        cluster.zoomToBounds({ padding: DEFAULT_PADDING });
      }
    });

    return badgeLayer;
  }

  if (typeof window.L.layerGroup === "function") {
    return window.L.layerGroup();
  }

  return null;
}

function clearTripMap(mapState) {
  const map = mapState?.map;
  if (!map) {
    return;
  }

  closeClusterPreviewTooltip(mapState);

  mapState.routeSegments.forEach((segmentEntry) => {
    segmentEntry.dispose?.();
  });

  mapState.routeLayers.forEach((layer) => {
    if (map.hasLayer?.(layer)) {
      map.removeLayer(layer);
    }
  });
  mapState.routeLayers = [];
  mapState.routeSegments = [];
  mapState.highlightedRouteSegmentIndex = null;
  mapState.highlightedRouteSegmentIndexes = [];

  if (typeof mapState.stopBadgeLayer?.clearLayers === "function") {
    mapState.stopBadgeLayer.clearLayers();
  }
  if (Array.isArray(mapState.badgeMarkers)) {
    mapState.badgeMarkers.forEach((marker) => {
      marker.closeTooltip?.();
      if (map.hasLayer?.(marker)) {
        map.removeLayer(marker);
      }
    });
  }
  mapState.badgeMarkers = [];
  if (mapState.badgeLayoutFrameId) {
    const hostWindow = resolveMapHostWindow(mapState);
    hostWindow?.cancelAnimationFrame?.(mapState.badgeLayoutFrameId);
    mapState.badgeLayoutFrameId = 0;
  }

  mapState.markers.forEach((marker) => {
    if (map.hasLayer?.(marker)) {
      map.removeLayer(marker);
    }
  });
  mapState.markers = [];
}

function applyRouteSegmentLayerStyles(mapState, segmentEntry, isHighlighted) {
  if (!segmentEntry || !hasMountedMap(mapState)) {
    return;
  }

  const styles = isHighlighted ? segmentEntry.highlightStyles : segmentEntry.defaultStyles;
  if (hasMountedLayer(mapState, segmentEntry.casing)) {
    segmentEntry.casing.setStyle(styles.casing);
  }
  if (hasMountedLayer(mapState, segmentEntry.line)) {
    segmentEntry.line.setStyle(styles.line);
  }
  segmentEntry.applyHighlightState?.(isHighlighted);

  if (isHighlighted) {
    if (hasMountedLayer(mapState, segmentEntry.casing)) {
      segmentEntry.casing.bringToFront();
    }
    if (hasMountedLayer(mapState, segmentEntry.line)) {
      segmentEntry.line.bringToFront();
    }
    segmentEntry.directionOverlay?.bringToFront?.();
  }
}

function buildSegmentNormal(startPoint, endPoint) {
  if (!startPoint || !endPoint) {
    return null;
  }

  const deltaX = endPoint.x - startPoint.x;
  const deltaY = endPoint.y - startPoint.y;
  const length = Math.hypot(deltaX, deltaY);
  if (length <= 1e-6) {
    return null;
  }

  return {
    x: -deltaY / length,
    y: deltaX / length,
  };
}

function toggleRouteSegmentLayerHighlight(layer, isHighlighted) {
  const element = layer?.getElement?.();
  if (!element?.classList) {
    return;
  }

  element.classList.toggle("route-map__segment--highlighted", isHighlighted);
}

function buildRouteSegmentPathId() {
  routeSegmentPathIdSequence += 1;
  return `route-map-segment-path-${routeSegmentPathIdSequence}`;
}

function shouldReduceRouteDirectionMotion(mapState) {
  const hostWindow = resolveMapHostWindow(mapState);
  return hostWindow?.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
}

function createRouteSegmentDirectionOverlay(mapState, lineLayer) {
  if (!hasMountedMap(mapState) || !lineLayer) {
    return null;
  }

  const lineElement = lineLayer.getElement?.();
  const svgRoot = lineElement?.ownerSVGElement;
  const ownerDocument = lineElement?.ownerDocument || mapState.ownerDocument;
  if (!lineElement || !svgRoot || !ownerDocument?.createElementNS) {
    return null;
  }

  if (!lineElement.id) {
    lineElement.id = buildRouteSegmentPathId();
  }

  const pathReference = `#${lineElement.id}`;
  const overlayGroup = ownerDocument.createElementNS(SVG_NAMESPACE, "g");
  overlayGroup.setAttribute("class", "route-map__segment route-map__segment--direction");
  overlayGroup.setAttribute("aria-hidden", "true");

  // En mode icône (Portail), les flèches sont toujours visibles sans attendre le highlight
  if (mapState.stopBadgePresentation === "icon") {
    overlayGroup.style.opacity = "0.65";
  }

  if (shouldReduceRouteDirectionMotion(mapState)) {
    const textElement = ownerDocument.createElementNS(SVG_NAMESPACE, "text");
    textElement.setAttribute("dy", "3");
    const textPath = ownerDocument.createElementNS(SVG_NAMESPACE, "textPath");
    textPath.setAttribute("href", pathReference);
    textPath.setAttributeNS(XLINK_NAMESPACE, "xlink:href", pathReference);
    textPath.setAttribute("startOffset", "8%");
    textPath.textContent = Array.from({ length: ROUTE_SEGMENT_DIRECTION_ARROW_COUNT }, () => ">").join("     ");
    textElement.appendChild(textPath);
    overlayGroup.appendChild(textElement);
  } else {
    const arrowSpacingSeconds = ROUTE_SEGMENT_DIRECTION_ANIMATION_DURATION_SECONDS / ROUTE_SEGMENT_DIRECTION_ARROW_COUNT;

    Array.from({ length: ROUTE_SEGMENT_DIRECTION_ARROW_COUNT }, (_, arrowIndex) => {
      const arrowGroup = ownerDocument.createElementNS(SVG_NAMESPACE, "g");
      arrowGroup.setAttribute("class", "route-map__segment--direction-arrow");

      const arrowPath = ownerDocument.createElementNS(SVG_NAMESPACE, "path");
      arrowPath.setAttribute("d", "M -4.5 -3.5 L 1 0 L -4.5 3.5");
      arrowPath.setAttribute("fill", "none");
      arrowPath.setAttribute("vector-effect", "non-scaling-stroke");
      arrowPath.setAttribute("stroke-linecap", "round");
      arrowPath.setAttribute("stroke-linejoin", "round");

      const animateMotion = ownerDocument.createElementNS(SVG_NAMESPACE, "animateMotion");
      animateMotion.setAttribute("dur", `${ROUTE_SEGMENT_DIRECTION_ANIMATION_DURATION_SECONDS}s`);
      animateMotion.setAttribute("repeatCount", "indefinite");
      animateMotion.setAttribute("rotate", "auto");
      animateMotion.setAttribute("calcMode", "paced");
      animateMotion.setAttribute("begin", `-${(arrowSpacingSeconds * arrowIndex).toFixed(2)}s`);

      const motionPath = ownerDocument.createElementNS(SVG_NAMESPACE, "mpath");
      motionPath.setAttribute("href", pathReference);
      motionPath.setAttributeNS(XLINK_NAMESPACE, "xlink:href", pathReference);
      animateMotion.appendChild(motionPath);

      const fade = ownerDocument.createElementNS(SVG_NAMESPACE, "animate");
      fade.setAttribute("attributeName", "opacity");
      fade.setAttribute("values", "0;1;1;0");
      fade.setAttribute("keyTimes", "0;0.12;0.88;1");
      fade.setAttribute("dur", `${ROUTE_SEGMENT_DIRECTION_ANIMATION_DURATION_SECONDS}s`);
      fade.setAttribute("repeatCount", "indefinite");
      fade.setAttribute("begin", animateMotion.getAttribute("begin"));

      arrowGroup.appendChild(arrowPath);
      arrowGroup.appendChild(animateMotion);
      arrowGroup.appendChild(fade);
      overlayGroup.appendChild(arrowGroup);
      return arrowGroup;
    });
  }

  svgRoot.appendChild(overlayGroup);

  return {
    bringToFront() {
      overlayGroup.parentNode?.appendChild(overlayGroup);
    },
    setHighlighted(isHighlighted) {
      overlayGroup.classList.toggle("route-map__segment--highlighted", isHighlighted);
      if (isHighlighted) {
        this.bringToFront();
      }
    },
    dispose() {
      overlayGroup.remove();
    },
  };
}

function buildRoutePolylineMidpointLatLng(map, latlngs = []) {
  if (!map || !Array.isArray(latlngs) || latlngs.length < 2) {
    return null;
  }

  const points = latlngs
    .filter((latlng) => Array.isArray(latlng) && latlng.length >= 2)
    .map((latlng) => window.L.latLng(latlng[0], latlng[1]));
  if (points.length < 2) {
    return null;
  }

  const segments = [];
  let totalLength = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const length = map.distance(points[index], points[index + 1]);
    if (!Number.isFinite(length) || length <= 0) {
      continue;
    }
    segments.push({ index, length });
    totalLength += length;
  }

  if (totalLength <= 0) {
    return null;
  }

  const targetLength = totalLength / 2;
  let accumulatedLength = 0;
  for (const segment of segments) {
    const startPoint = points[segment.index];
    const endPoint = points[segment.index + 1];
    const nextLength = accumulatedLength + segment.length;
    if (targetLength <= nextLength) {
      const fraction = segment.length > 0 ? (targetLength - accumulatedLength) / segment.length : 0.5;
      return [
        startPoint.lat + ((endPoint.lat - startPoint.lat) * fraction),
        startPoint.lng + ((endPoint.lng - startPoint.lng) * fraction),
      ];
    }
    accumulatedLength = nextLength;
  }

  const lastPoint = points[points.length - 1];
  return [lastPoint.lat, lastPoint.lng];
}

function createRouteDistanceOverlay(mapState, latlngs = [], distanceLabel = "") {
  if (!hasMountedMap(mapState) || typeof distanceLabel !== "string" || !distanceLabel.trim()) {
    return null;
  }

  const midpointLatLng = buildRoutePolylineMidpointLatLng(mapState.map, latlngs);
  if (!midpointLatLng) {
    return null;
  }

  const normalizedDistanceLabel = distanceLabel.trim();
  const marker = window.L.marker(midpointLatLng, {
    interactive: false,
    keyboard: false,
    zIndexOffset: ROUTE_DISTANCE_LABEL_Z_INDEX_OFFSET,
    icon: window.L.divIcon({
      className: "route-map-route-distance-icon",
      html: '<span class="route-map-route-distance__pill">' + escapeRouteMapHtml(normalizedDistanceLabel) + '</span>',
      iconAnchor: [0, 0],
      iconSize: [1, 1],
    }),
  });

  marker.addTo(mapState.map);
  return marker;
}

function resolveOffsetNormal(previousPoint, point, nextPoint) {
  const previousNormal = previousPoint ? buildSegmentNormal(previousPoint, point) : null;
  const nextNormal = nextPoint ? buildSegmentNormal(point, nextPoint) : null;

  if (previousNormal && nextNormal) {
    const mergedX = previousNormal.x + nextNormal.x;
    const mergedY = previousNormal.y + nextNormal.y;
    const mergedLength = Math.hypot(mergedX, mergedY);
    if (mergedLength > 1e-6) {
      return {
        x: mergedX / mergedLength,
        y: mergedY / mergedLength,
      };
    }
  }

  return nextNormal || previousNormal || null;
}

function buildOffsetRouteLatLngs(map, latlngs, offsetPixels) {
  if (!map || !Array.isArray(latlngs) || latlngs.length < 2 || !Number.isFinite(offsetPixels) || offsetPixels === 0) {
    return latlngs;
  }

  const layerPoints = latlngs.map((latlng) => map.latLngToLayerPoint(window.L.latLng(latlng[0], latlng[1])));
  return layerPoints.map((point, index) => {
    const previousPoint = index > 0 ? layerPoints[index - 1] : null;
    const nextPoint = index < layerPoints.length - 1 ? layerPoints[index + 1] : null;
    const normal = resolveOffsetNormal(previousPoint, point, nextPoint);
    if (!normal) {
      return latlngs[index];
    }

    const shiftedPoint = window.L.point(
      point.x + (normal.x * offsetPixels),
      point.y + (normal.y * offsetPixels),
    );
    const shiftedLatLng = map.layerPointToLatLng(shiftedPoint);
    return [shiftedLatLng.lat, shiftedLatLng.lng];
  });
}

function buildRouteGeometrySignature(latlngs) {
  if (!Array.isArray(latlngs) || latlngs.length < 2) {
    return null;
  }

  const forward = latlngs
    .map((latlng) => `${latlng[0].toFixed(5)},${latlng[1].toFixed(5)}`)
    .join("|");
  const reversed = latlngs
    .slice()
    .reverse()
    .map((latlng) => `${latlng[0].toFixed(5)},${latlng[1].toFixed(5)}`)
    .join("|");

  return forward < reversed ? forward : reversed;
}

function buildRouteOverlapOffsets(count) {
  if (!Number.isInteger(count) || count <= 1) {
    return [0];
  }

  const step = 8;
  return Array.from({ length: count }, (_, index) => (index - ((count - 1) / 2)) * step);
}

function resolveOverlappingRouteSegmentOffsets(routeSegments) {
  const overlapOffsets = new Map();
  const groupsBySignature = new Map();

  routeSegments.forEach((segmentEntry) => {
    const signature = buildRouteGeometrySignature(segmentEntry.baseLatLngs);
    if (!signature) {
      overlapOffsets.set(segmentEntry, 0);
      return;
    }

    const group = groupsBySignature.get(signature) || [];
    group.push(segmentEntry);
    groupsBySignature.set(signature, group);
  });

  groupsBySignature.forEach((group) => {
    const sortedGroup = group.slice().sort((left, right) => {
      const leftIndex = Number.isInteger(left.index) ? left.index : Number.MAX_SAFE_INTEGER;
      const rightIndex = Number.isInteger(right.index) ? right.index : Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });

    const offsets = buildRouteOverlapOffsets(sortedGroup.length);
    sortedGroup.forEach((segmentEntry, index) => {
      overlapOffsets.set(segmentEntry, offsets[index]);
    });
  });

  return overlapOffsets;
}

function refreshRouteSegmentOffsets(mapState) {
  if (!hasMountedMap(mapState) || !Array.isArray(mapState.routeSegments) || !mapState.routeSegments.length) {
    return;
  }

  const overlapOffsets = resolveOverlappingRouteSegmentOffsets(mapState.routeSegments);
  mapState.routeSegments.forEach((segmentEntry) => {
    if (!hasMountedLayer(mapState, segmentEntry.casing) || !hasMountedLayer(mapState, segmentEntry.line)) {
      return;
    }

    const offsetPixels = overlapOffsets.get(segmentEntry) ?? 0;
    segmentEntry.offsetPixels = offsetPixels;
    const displayLatLngs = offsetPixels === 0
      ? segmentEntry.baseLatLngs
      : buildOffsetRouteLatLngs(mapState.map, segmentEntry.baseLatLngs, offsetPixels);
    segmentEntry.casing.setLatLngs(displayLatLngs);
    segmentEntry.line.setLatLngs(displayLatLngs);
  });

  scheduleRouteBadgeRefresh(mapState);

  const highlightedRouteSegmentIndexSet = getHighlightedRouteSegmentIndexSet(mapState);
  mapState.routeSegments.forEach((segmentEntry) => {
    applyRouteSegmentLayerStyles(mapState, segmentEntry, highlightedRouteSegmentIndexSet.has(segmentEntry.index));
  });
}

function getHighlightedRouteSegmentIndexSet(mapState) {
  const highlightedIndexes = Array.isArray(mapState?.highlightedRouteSegmentIndexes)
    ? mapState.highlightedRouteSegmentIndexes
    : [];
  const resolvedIndexes = highlightedIndexes.length
    ? highlightedIndexes
    : [mapState?.highlightedRouteSegmentIndex];

  return new Set(
    resolvedIndexes
      .map((routeSegmentIndex) => Number.parseInt(routeSegmentIndex, 10))
      .filter((routeSegmentIndex) => Number.isInteger(routeSegmentIndex) && routeSegmentIndex > 0),
  );
}

function isRouteSegmentIndexHighlighted(mapState, routeSegmentIndex) {
  return getHighlightedRouteSegmentIndexSet(mapState).has(routeSegmentIndex);
}

function addTripRouteLayers(
  mapState,
  routeLatLngs,
  routeSegments,
  exactRouteSegmentLatLngGroups = [],
  routeDistanceLabel = "",
  aspiratorRouteSegmentIndexes = [],
) {
  const exactSegments = buildExactRoutePolylineSegments(exactRouteSegmentLatLngGroups, routeSegments);
  const baseColoredSegments = exactSegments.length
    ? exactSegments
    : buildRoutePolylineSegments(routeLatLngs, routeSegments);
  const coloredSegments = applyAspiratorRouteSegmentColors(
    baseColoredSegments,
    aspiratorRouteSegmentIndexes,
  );
  const segmentsToRender = coloredSegments.length
    ? coloredSegments
    : [{ latlngs: routeLatLngs, color: DEPOT_ROUTE_COLOR, routeSegmentIndex: routeSegments[0]?.index ?? null }];
  const routeCasingColor = "#000000";

  segmentsToRender.forEach((segment) => {
    const segmentColor = segment.color || DEPOT_ROUTE_COLOR;
    const casing = window.L.polyline(segment.latlngs, {
      className: "route-map__segment route-map__segment--casing",
      color: routeCasingColor,
      weight: 9,
      opacity: 1,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(mapState.map);
    const line = window.L.polyline(segment.latlngs, {
      className: "route-map__segment route-map__segment--line",
      color: segmentColor,
      weight: 5.5,
      opacity: 0.98,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(mapState.map);
    const routeSegmentIndex = Number.isInteger(segment.routeSegmentIndex)
      ? segment.routeSegmentIndex
      : Number.isInteger(segment.index)
        ? segment.index
        : null;
    const segmentEntry = {
      index: routeSegmentIndex,
      baseLatLngs: segment.latlngs.map((latlng) => [latlng[0], latlng[1]]),
      offsetPixels: 0,
      casing,
      line,
      directionOverlay: createRouteSegmentDirectionOverlay(mapState, line),
      applyHighlightState(isHighlighted) {
        toggleRouteSegmentLayerHighlight(casing, isHighlighted);
        toggleRouteSegmentLayerHighlight(line, isHighlighted);
        segmentEntry.directionOverlay?.setHighlighted?.(isHighlighted);
      },
      dispose() {
        segmentEntry.directionOverlay?.dispose?.();
      },
      defaultStyles: {
        casing: { color: routeCasingColor, weight: 9, opacity: 1 },
        line: { color: segmentColor, weight: 5.5, opacity: 0.98 },
      },
      highlightStyles: {
        casing: { color: routeCasingColor, weight: 10.6, opacity: 1 },
        line: { color: segmentColor, weight: 7.4, opacity: 1 },
      },
    };

    const syncSegmentDomState = () => {
      if (!hasMountedLayer(mapState, casing) || !hasMountedLayer(mapState, line)) {
        return false;
      }

      if (!segmentEntry.directionOverlay) {
        segmentEntry.directionOverlay = createRouteSegmentDirectionOverlay(mapState, line);
      }

      applyRouteSegmentLayerStyles(
        mapState,
        segmentEntry,
        isRouteSegmentIndexHighlighted(mapState, routeSegmentIndex),
      );
      return true;
    };

    syncSegmentDomState();
    resolveMapHostWindow(mapState)?.requestAnimationFrame?.(() => {
      syncSegmentDomState();
    });
    mapState.routeLayers.push(casing, line);
    mapState.routeSegments.push(segmentEntry);
  });

  const distanceOverlay = createRouteDistanceOverlay(mapState, segmentsToRender[0]?.latlngs || routeLatLngs, routeDistanceLabel);
  if (distanceOverlay) {
    mapState.routeLayers.push(distanceOverlay);
  }

  refreshRouteSegmentOffsets(mapState);
}

function addTripStopRouteNumberBadge({
  assignment,
  entry,
  isGroupedStop = false,
  mapState,
  onClientSelect,
  nextEntry = null,
  previousEntry = null,
  routeLabel = "",
  routeNumber,
  showHouse = false,
  stopBadgePresentation = "number",
  trip,
}) {
  const isIconPresentation = stopBadgePresentation === "icon";
  if (!mapState || !entry || (!isIconPresentation && !Number.isInteger(routeNumber) && !showHouse) || !window.L) {
    return;
  }

  const normalizedRouteLabel = typeof routeLabel === "string" && routeLabel.trim()
    ? routeLabel.trim()
    : (Number.isInteger(routeNumber) ? String(routeNumber) : "");
  const badgeRole = typeof entry?.stop?.badge_role === "string" ? entry.stop.badge_role.trim() : "";
  const tooltipLabel = buildStopTooltipLabel(entry.stop, normalizedRouteLabel);
  const clientId = getClientSelectionId(entry.stop);
  const badgeHtml = buildRouteBadgeHtml({
    routeLabel: normalizedRouteLabel,
    showHouse,
    isGroupedStop,
    stop: entry.stop,
    stopType: entry.stop?.stop_type || "",
    presentation: stopBadgePresentation,
  });
  const iconAnchor = isIconPresentation
    ? [Math.round(ICON_TEXT_BADGE_WIDTH / 2), Math.round(ICON_TEXT_BADGE_HEIGHT / 2)]
    : showHouse
    ? normalizedRouteLabel
      ? [54, 30]
      : [23, 60]
    : [0, 8];
  const iconSize = isIconPresentation
    ? [ICON_TEXT_BADGE_WIDTH, ICON_TEXT_BADGE_HEIGHT]
    : showHouse
    ? normalizedRouteLabel
      ? [78, 60]
      : [49, 60]
    : [normalizedRouteLabel.length > 2 ? 36 : 18, 18];

  const badgeMarker = window.L.marker(entry.latlng, {
    interactive: true,
    keyboard: false,
    zIndexOffset: 200,
    icon: window.L.divIcon({
      className: buildRouteBadgeClassName(routeNumber),
      html: badgeHtml,
      iconAnchor,
      iconSize,
    }),
  });

  badgeMarker.bindTooltip(tooltipLabel, {
    className: "route-stop-tooltip",
    direction: "top",
    offset: [0, -8],
    opacity: 1,
  });

  badgeMarker.__clientId = clientId;
  badgeMarker.__isGroupedStop = isGroupedStop;
  badgeMarker.__routeLabel = normalizedRouteLabel;
  badgeMarker.__routeNumber = Number.isInteger(routeNumber) ? routeNumber : null;
  badgeMarker.__stopType = entry.stop?.stop_type || "";
  badgeMarker.__tooltipLabel = tooltipLabel;
  badgeMarker.__nextStopLatLng = nextEntry?.latlng ? [nextEntry.latlng.lat, nextEntry.latlng.lng] : null;
  badgeMarker.__previousStopLatLng = previousEntry?.latlng ? [previousEntry.latlng.lat, previousEntry.latlng.lng] : null;

  if (clientId && typeof onClientSelect === "function") {
    badgeMarker.on("click", () => {
      onClientSelect({
        assignmentId: assignment?.truck_id || null,
        centerMap: true,
        clientId,
        tripId: trip?.trip_id || null,
      });
    });
  }

  const badgeLayer = isIconPresentation && badgeRole === "origin"
    ? mapState.map
    : (mapState.stopBadgeLayer || mapState.map);
  if (badgeLayer && typeof badgeLayer.addLayer === "function") {
    badgeLayer.addLayer(badgeMarker);
  } else if (badgeLayer && typeof badgeMarker.addTo === "function") {
    badgeMarker.addTo(badgeLayer);
  }

  mapState.badgeMarkers.push(badgeMarker);
  scheduleRouteBadgeRefresh(mapState);
}

function addTripStopMarkers({
  assignment,
  mapState,
  onClientSelect,
  routeBadgeMetaByStopKey = new Map(),
  stopsWithCoordinates,
  trip,
}) {
  stopsWithCoordinates.forEach((entry, index) => {
    const isIconPresentation = mapState?.stopBadgePresentation === "icon";
    const stopKey = buildRouteMapStopKey(entry.stop, index);
    const mappedBadgeMeta = stopKey ? routeBadgeMetaByStopKey.get(stopKey) : null;
    const routeNumber = Number.isInteger(mappedBadgeMeta?.routeNumber)
      ? mappedBadgeMeta.routeNumber
      : getRouteNumberForStopIndex(index);
    const routeLabel = typeof mappedBadgeMeta?.routeLabel === "string" && mappedBadgeMeta.routeLabel.trim()
      ? mappedBadgeMeta.routeLabel.trim()
      : (Number.isInteger(routeNumber) ? String(routeNumber) : "");
    const tooltipLabel = buildStopTooltipLabel(entry.stop, routeLabel);
    const clientId = getClientSelectionId(entry.stop);
    const radius = index === 0 || index === stopsWithCoordinates.length - 1 ? 8 : 6;
    const stopFillColor = isIconPresentation ? getStopDisplayColor(entry.stop) : "#ffffff";
    const stopStrokeColor = isIconPresentation ? "#ffffff" : "#12182a";
    const marker = window.L.circleMarker(entry.latlng, {
      pane: "routeStops",
      radius,
      weight: 2,
      color: stopStrokeColor,
      fillColor: stopFillColor,
      fillOpacity: 0.98,
      opacity: 1,
    }).addTo(mapState.map);
    marker.bindTooltip(tooltipLabel, {
      className: "route-stop-tooltip",
      direction: "top",
      offset: [0, -8],
      opacity: 1,
    });
    marker.__defaultStyle = {
      radius,
      weight: 2,
      color: stopStrokeColor,
      fillColor: stopFillColor,
      fillOpacity: 0.98,
      opacity: 1,
    };
    marker.__clientId = clientId;
    marker.__stopType = entry.stop?.stop_type || "";
    if (clientId && typeof onClientSelect === "function") {
      marker.on("click", () => {
        onClientSelect({
          assignmentId: assignment?.truck_id || null,
          centerMap: true,
          clientId,
          tripId: trip?.trip_id || null,
        });
      });
    }
    if (mappedBadgeMeta?.shouldRenderBadge === false && !isIconPresentation) {
      mapState.markers.push(marker);
      return;
    }

    addTripStopRouteNumberBadge({
      assignment,
      entry,
      isGroupedStop: mappedBadgeMeta?.usesGroupedRangeLabel === true,
      mapState,
      nextEntry: stopsWithCoordinates[index + 1] || null,
      onClientSelect,
      previousEntry: stopsWithCoordinates[index - 1] || null,
      routeLabel,
      routeNumber,
      showHouse: entry.stop?.stop_type === "depot_start",
      stopBadgePresentation: mapState.stopBadgePresentation,
      trip,
    });

    mapState.markers.push(marker);
  });
}

function createRouteMapState(mapContainer, stopBadgePresentation = "number") {
  if (!isHtmlElement(mapContainer) || !window.L) {
    return null;
  }

  const map = window.L.map(mapContainer, {
    attributionControl: true,
    minZoom: 1,
    maxZoom: 19,
    fadeAnimation: false,
    markerZoomAnimation: false,
    scrollWheelZoom: true,
    zoomControl: false,
    zoomAnimation: false,
  });

  // Keep stop points above route strokes and direction arrows, including on hover.
  map.createPane("routeStops").style.zIndex = "450";

  const stopBadgeLayer = createStopBadgeLayer(map, stopBadgePresentation);
  if (stopBadgeLayer && typeof stopBadgeLayer.addTo === "function") {
    stopBadgeLayer.addTo(map);
  }

  map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  const disposeBasemap = attachRouteMapBasemap(map);

  const mapState = {
    map,
    disposeBasemap,
    mapContainer,
    ownerDocument: mapContainer.ownerDocument || null,
    clusterPreviewTooltip: null,
    badgeMarkers: [],
    routeLayers: [],
    routeSegments: [],
    markers: [],
    stopBadgeLayer,
    stopBadgePresentation,
    badgeLayoutFrameId: 0,
    resizeFrameId: 0,
    isDisposed: false,
    highlightedRouteSegmentIndex: null,
    highlightedRouteSegmentIndexes: [],
    baseView: { mode: "view", center: [...DEFAULT_CENTER], zoom: DEFAULT_ZOOM },
  };

  map.on("zoomend", () => {
    refreshRouteSegmentOffsets(mapState);
    scheduleRouteBadgeRefresh(mapState);
  });
  map.on("moveend", () => {
    refreshRouteSegmentOffsets(mapState);
    scheduleRouteBadgeRefresh(mapState);
  });
  scheduleRouteBadgeRefresh(mapState);

  return mapState;
}

function destroyRouteMap(mapState) {
  if (!mapState?.map || mapState.isDisposed) {
    return;
  }

  const hostWindow = resolveMapHostWindow(mapState);
  if (mapState.resizeFrameId) {
    hostWindow?.cancelAnimationFrame?.(mapState.resizeFrameId);
    mapState.resizeFrameId = 0;
  }
  if (mapState.badgeLayoutFrameId) {
    hostWindow?.cancelAnimationFrame?.(mapState.badgeLayoutFrameId);
    mapState.badgeLayoutFrameId = 0;
  }

  mapState.isDisposed = true;
  mapState.disposeBasemap?.();
  mapState.map.stop?.();
  clearTripMap(mapState);
  mapState.map.off();
  mapState.map.remove();
  mapState.map = null;
}

function ensureRouteMap(mapContainer, mapStateRef, stopBadgePresentation = "number") {
  const currentMapState = mapStateRef.current;
  if (currentMapState?.map) {
    const currentMapContainer = typeof currentMapState.map.getContainer === "function"
      ? currentMapState.map.getContainer()
      : currentMapState.mapContainer;
    const nextOwnerDocument = isHtmlElement(mapContainer) ? mapContainer.ownerDocument || null : null;

    if (currentMapContainer === mapContainer && currentMapState.ownerDocument === nextOwnerDocument) {
        if (currentMapState.stopBadgePresentation === stopBadgePresentation) {
          return currentMapState;
        }

        destroyRouteMap(currentMapState);
        mapStateRef.current = null;
    }

    destroyRouteMap(currentMapState);
    mapStateRef.current = null;
  }

  const nextMapState = createRouteMapState(mapContainer, stopBadgePresentation);
  if (nextMapState) {
    mapStateRef.current = nextMapState;
  }

  return mapStateRef.current;
}

function scheduleMapResize(mapState) {
  if (!hasMountedMap(mapState)) {
    return;
  }

  const hostWindow = resolveMapHostWindow(mapState);
  if (!hostWindow?.requestAnimationFrame) {
    return;
  }

  const mapContainer = typeof mapState.map.getContainer === "function"
    ? mapState.map.getContainer()
    : null;
  if (!isHtmlElement(mapContainer) || !mapContainer.isConnected) {
    return;
  }

  if (mapState.resizeFrameId) {
    hostWindow.cancelAnimationFrame(mapState.resizeFrameId);
  }

  mapState.resizeFrameId = hostWindow.requestAnimationFrame(() => {
    mapState.resizeFrameId = 0;
    if (!hasMountedMap(mapState)) {
      return;
    }

    const nextMapContainer = typeof mapState.map.getContainer === "function"
      ? mapState.map.getContainer()
      : null;
    if (!isHtmlElement(nextMapContainer) || !nextMapContainer.isConnected) {
      return;
    }

    mapState.map.invalidateSize();
    scheduleRouteBadgeRefresh(mapState);
  });
}

function renderTripMap({
  assignment,
  mapContainer,
  mapStateRef,
  onClientSelect,
  routeAlternativePreview = null,
  routeDistanceLabel = "",
  showGroupedStopRangeLabels = false,
  state = null,
  stopBadgePresentation = "number",
  trip,
}) {
  const mapState = ensureRouteMap(mapContainer, mapStateRef, stopBadgePresentation);
  if (!mapState?.map) {
    return {
      rendered: false,
      category: "no-map",
      displayLabel: "Carte Leaflet indisponible",
      routePointCount: 0,
      markerCount: 0,
    };
  }

  clearTripMap(mapState);

  if (!trip) {
    mapState.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    rememberMapBaseView(mapState);
    return buildDefaultMapRenderMeta();
  }

  const routableStops = getTripRoutableStops(trip);
  const stopsWithCoordinates = collectStopsWithCoordinates(routableStops);
  const stopLatLngs = stopsWithCoordinates.map((entry) => entry.latlng);
  const routeSegments = getTripRouteSegments(trip, assignment);
  const aspiratorRouteSegmentIndexes = collectAspiratorMissionRouteSegmentIndexes(
    buildTripTimelineSegments(trip, assignment, { state }),
  );
  const routeBadgeMetaByStopKey = buildRouteBadgeMetaByStopKey(assignment, trip, {
    showGroupedStopRangeLabels,
  });
  const exactRouteSegmentLatLngGroups = collectRouteSegmentLatLngGroups(trip.route?.segment_geometries);
  const hasAlignedSegmentGeometries = exactRouteSegmentLatLngGroups.length > 0
    && (!routeSegments.length || exactRouteSegmentLatLngGroups.length === routeSegments.length);
  const routeLatLngs = hasAlignedSegmentGeometries
    ? flattenRouteSegmentLatLngGroups(exactRouteSegmentLatLngGroups)
    : collectRouteLatLngs(trip.route?.geometry);
  const fallbackRouteLatLngs = routableStops
    .filter(hasStopCoordinates)
    .map((stop) => [stop.latitude, stop.longitude]);

  if (routeLatLngs.length > 1) {
    addTripRouteLayers(
      mapState,
      routeLatLngs,
      routeSegments,
      hasAlignedSegmentGeometries ? exactRouteSegmentLatLngGroups : [],
      routeDistanceLabel,
      aspiratorRouteSegmentIndexes,
    );
    const overlayLatLngs = addRouteAlternativeOverlayLayers(mapState, trip, routeAlternativePreview);
    const boundsLatLngs = overlayLatLngs.length ? routeLatLngs.concat(overlayLatLngs) : routeLatLngs;
    addTripStopMarkers({
      assignment,
      mapState,
      onClientSelect,
      routeBadgeMetaByStopKey,
      stopsWithCoordinates,
      trip,
    });
    mapState.map.fitBounds(window.L.latLngBounds(boundsLatLngs), { padding: DEFAULT_PADDING });
    rememberMapBaseView(mapState, { mode: "bounds", bounds: boundsLatLngs, padding: DEFAULT_PADDING });
    scheduleMapResize(mapState);
    return {
      rendered: true,
      category: "success",
      displayLabel: `Trace de trajet affichee (${routeLatLngs.length} points)`,
      routePointCount: routeLatLngs.length,
      markerCount: stopLatLngs.length,
    };
  }

  if (fallbackRouteLatLngs.length > 1) {
    addTripRouteLayers(
      mapState,
      fallbackRouteLatLngs,
      routeSegments,
      [],
      routeDistanceLabel,
      aspiratorRouteSegmentIndexes,
    );
    const overlayLatLngs = addRouteAlternativeOverlayLayers(mapState, trip, routeAlternativePreview);
    const boundsLatLngs = overlayLatLngs.length ? fallbackRouteLatLngs.concat(overlayLatLngs) : fallbackRouteLatLngs;
    addTripStopMarkers({
      assignment,
      mapState,
      onClientSelect,
      routeBadgeMetaByStopKey,
      stopsWithCoordinates,
      trip,
    });
    mapState.map.fitBounds(window.L.latLngBounds(boundsLatLngs), { padding: DEFAULT_PADDING });
    rememberMapBaseView(mapState, { mode: "bounds", bounds: boundsLatLngs, padding: DEFAULT_PADDING });
    scheduleMapResize(mapState);
    return {
      rendered: true,
      category: "heuristic",
      displayLabel: `Route active reconstruite (${fallbackRouteLatLngs.length} points)`,
      routePointCount: fallbackRouteLatLngs.length,
      markerCount: stopLatLngs.length,
    };
  }

  addTripStopMarkers({
    assignment,
    mapState,
    onClientSelect,
    routeBadgeMetaByStopKey,
    stopsWithCoordinates,
    trip,
  });

  if (trip.route?.status === "success" && trip.route?.geometry_available) {
    if (stopLatLngs.length > 0) {
      focusMapOnStopLatLngs(mapState, stopLatLngs);
    } else {
      mapState.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      rememberMapBaseView(mapState);
    }
    scheduleMapResize(mapState);
    return {
      rendered: false,
      category: "frontend",
      displayLabel: "Géométrie reçue mais non interprétée",
      routePointCount: routeLatLngs.length,
      markerCount: stopLatLngs.length,
    };
  }

  if (stopLatLngs.length > 0) {
    focusMapOnStopLatLngs(mapState, stopLatLngs);
    scheduleMapResize(mapState);
    return {
      rendered: false,
      category: "stops-only",
      displayLabel: `Points de tournée affichés (${stopLatLngs.length})`,
      routePointCount: routeLatLngs.length,
      markerCount: stopLatLngs.length,
    };
  }

  mapState.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  rememberMapBaseView(mapState);
  return {
    rendered: false,
    category: "no-coordinates",
    displayLabel: "Aucune coordonnée exploitable pour la carte",
    routePointCount: routeLatLngs.length,
    markerCount: 0,
  };
}

export default function useRouteMap({
  assignment = null,
  isRouteViewActive = true,
  onClientSelect = null,
  routeAlternativePreview = null,
  routeDistanceLabel = "",
  showGroupedStopRangeLabels = false,
  stateRef,
  stopBadgePresentation = "number",
  trip = null,
}) {
  const [mapContainerElement, setMapContainerElement] = useState(null);
  const mapStateRef = useRef(null);
  const latestClientSelectRef = useRef(onClientSelect);
  const previousRouteViewActiveRef = useRef(isRouteViewActive);
  const [mapRenderMeta, setMapRenderMeta] = useState(buildDefaultMapRenderMeta);
  const mapContainerRef = useCallback((element) => {
    setMapContainerElement((currentElement) => (currentElement === element ? currentElement : element || null));
  }, []);

  useEffect(() => {
    latestClientSelectRef.current = onClientSelect;
  }, [onClientSelect]);

  useEffect(() => () => {
    destroyRouteMap(mapStateRef.current);
    mapStateRef.current = null;
  }, []);

  useEffect(() => {
    if (!isRouteViewActive) {
      return;
    }

    const nextMapRenderMeta = renderTripMap({
      assignment,
      mapContainer: mapContainerElement,
      mapStateRef,
      onClientSelect: (selection) => {
        latestClientSelectRef.current?.(selection);
      },
      routeAlternativePreview,
      routeDistanceLabel,
      showGroupedStopRangeLabels,
      state: stateRef?.current || null,
      stopBadgePresentation,
      trip,
    });

    setMapRenderMeta(nextMapRenderMeta);
  }, [assignment, isRouteViewActive, mapContainerElement, routeAlternativePreview, routeDistanceLabel, showGroupedStopRangeLabels, stateRef, stopBadgePresentation, trip]);

  useEffect(() => {
    const wasRouteViewActive = previousRouteViewActiveRef.current;
    previousRouteViewActiveRef.current = isRouteViewActive;

    if (!isRouteViewActive || wasRouteViewActive || typeof window === "undefined") {
      return undefined;
    }

    const mapState = mapStateRef.current;
    if (!hasMountedMap(mapState)) {
      return undefined;
    }

    const hostWindow = resolveMapHostWindow(mapState);
    if (!hostWindow?.requestAnimationFrame) {
      return undefined;
    }

    let frameId = 0;
    let cancelled = false;
    let attempts = 0;

    const restoreWhenVisible = () => {
      if (cancelled || !hasMountedMap(mapState)) {
        return;
      }

      const mapContainer = typeof mapState.map.getContainer === "function"
        ? mapState.map.getContainer()
        : null;
      if (!isHtmlElement(mapContainer) || !mapContainer.isConnected) {
        return;
      }

      const bounds = mapContainer.getBoundingClientRect();
      if ((bounds.width <= 0 || bounds.height <= 0) && attempts < 4) {
        attempts += 1;
        frameId = hostWindow.requestAnimationFrame(restoreWhenVisible);
        return;
      }

      mapState.map.invalidateSize(false);
      restoreMapBaseView(mapState);
    };

    frameId = hostWindow.requestAnimationFrame(restoreWhenVisible);

    return () => {
      cancelled = true;
      if (frameId) {
        hostWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [isRouteViewActive]);

  useEffect(() => {
    const mapElement = mapContainerElement;
    if (!isHtmlElement(mapElement)) {
      return undefined;
    }

    const elementWindow = resolveElementWindow(mapElement);

    const handleContainerResize = () => {
      scheduleMapResize(mapStateRef.current);
    };

    handleContainerResize();

    if (typeof ResizeObserver === "undefined") {
      if (!elementWindow) {
        return undefined;
      }

      elementWindow.addEventListener("resize", handleContainerResize);

      return () => {
        elementWindow.removeEventListener("resize", handleContainerResize);
      };
    }

    const observer = new ResizeObserver(() => {
      handleContainerResize();
    });

    observer.observe(mapElement);

    return () => {
      observer.disconnect();
    };
  }, [mapContainerElement]);

  useEffect(() => {
    const mapElement = mapContainerElement;
    if (!isHtmlElement(mapElement)) {
      return undefined;
    }

    const handlePreviewPointerOver = (event) => {
      const previewElement = isHtmlElement(event.target)
        ? event.target.closest(".route-map-stop-cluster__preview")
        : null;
      if (!isHtmlElement(previewElement) || !mapElement.contains(previewElement)) {
        return;
      }

      openClusterPreviewTooltip(mapStateRef.current, previewElement);
    };

    const handlePreviewPointerOut = (event) => {
      const previewElement = isHtmlElement(event.target)
        ? event.target.closest(".route-map-stop-cluster__preview")
        : null;
      if (!isHtmlElement(previewElement) || !mapElement.contains(previewElement)) {
        return;
      }

      const nextPreviewElement = isHtmlElement(event.relatedTarget)
        ? event.relatedTarget.closest(".route-map-stop-cluster__preview")
        : null;
      if (isHtmlElement(nextPreviewElement) && mapElement.contains(nextPreviewElement)) {
        openClusterPreviewTooltip(mapStateRef.current, nextPreviewElement);
        return;
      }

      closeClusterPreviewTooltip(mapStateRef.current);
    };

    mapElement.addEventListener("pointerover", handlePreviewPointerOver);
    mapElement.addEventListener("pointerout", handlePreviewPointerOut);

    return () => {
      mapElement.removeEventListener("pointerover", handlePreviewPointerOver);
      mapElement.removeEventListener("pointerout", handlePreviewPointerOut);
      closeClusterPreviewTooltip(mapStateRef.current);
    };
  }, [mapContainerElement]);

  useEffect(() => {
    const mapElement = mapContainerElement;
    const mapState = mapStateRef.current;
    if (!isHtmlElement(mapElement) || !hasMountedMap(mapState)) {
      return undefined;
    }

    if (!mapState.map.dragging) {
      return undefined;
    }

    if (!shouldUseDetachedMapPointerPan(mapElement)) {
      mapState.map.dragging.enable?.();
      return undefined;
    }

    mapState.map.stop?.();
    mapState.map.dragging.disable?.();

    return () => {
      if (!hasMountedMap(mapState)) {
        return;
      }

      mapState.map.dragging.enable?.();
    };
  }, [mapContainerElement]);

  useEffect(() => {
    const mapElement = mapContainerElement;
    if (!shouldUseDetachedMapPointerPan(mapElement)) {
      return undefined;
    }

    const hostWindow = resolveElementWindow(mapElement);
    if (!hostWindow) {
      return undefined;
    }

    let pointerState = null;

    const handlePointerDown = (event) => {
      if (event.button !== 0 || shouldIgnoreDetachedMapPanTarget(event.target)) {
        return;
      }

      const mapState = mapStateRef.current;
      if (!hasMountedMap(mapState)) {
        return;
      }

      pointerState = {
        hasDragged: false,
        lastX: event.clientX,
        lastY: event.clientY,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };

      try {
        mapElement.setPointerCapture?.(event.pointerId);
      } catch {
        // Ignore browsers that do not expose pointer capture in this context.
      }
    };

    const clearPointerState = (event = null, { force = false } = {}) => {
      if (!pointerState) {
        return;
      }

      if (!force && event && event.pointerId !== pointerState.pointerId) {
        return;
      }

      const activePointerId = pointerState.pointerId;

      try {
        if (mapElement.hasPointerCapture?.(activePointerId)) {
          mapElement.releasePointerCapture(activePointerId);
        }
      } catch {
        // Ignore browsers that already released the pointer.
      }

      pointerState = null;
    };

    const handlePointerMove = (event) => {
      if (!pointerState || event.pointerId !== pointerState.pointerId) {
        return;
      }

      if (!isPrimaryPointerButtonPressed(event)) {
        clearPointerState(event);
        return;
      }

      const mapState = mapStateRef.current;
      if (!hasMountedMap(mapState)) {
        clearPointerState(event, { force: true });
        return;
      }

      const totalDeltaX = event.clientX - pointerState.startX;
      const totalDeltaY = event.clientY - pointerState.startY;
      if (!pointerState.hasDragged && Math.hypot(totalDeltaX, totalDeltaY) < DETACHED_MAP_DRAG_THRESHOLD_PX) {
        return;
      }

      pointerState.hasDragged = true;
      const deltaX = event.clientX - pointerState.lastX;
      const deltaY = event.clientY - pointerState.lastY;
      pointerState.lastX = event.clientX;
      pointerState.lastY = event.clientY;

      event.preventDefault();
      mapState.map.panBy([-deltaX, -deltaY], { animate: false });
    };

    const handleWindowBlur = () => {
      clearPointerState(null, { force: true });
    };

    mapElement.addEventListener("pointerdown", handlePointerDown);
    mapElement.addEventListener("lostpointercapture", clearPointerState);
    hostWindow.addEventListener("pointermove", handlePointerMove, true);
    hostWindow.addEventListener("pointerup", clearPointerState, true);
    hostWindow.addEventListener("pointercancel", clearPointerState, true);
    hostWindow.addEventListener("blur", handleWindowBlur);

    return () => {
      mapElement.removeEventListener("pointerdown", handlePointerDown);
      mapElement.removeEventListener("lostpointercapture", clearPointerState);
      hostWindow.removeEventListener("pointermove", handlePointerMove, true);
      hostWindow.removeEventListener("pointerup", clearPointerState, true);
      hostWindow.removeEventListener("pointercancel", clearPointerState, true);
      hostWindow.removeEventListener("blur", handleWindowBlur);
      clearPointerState(null, { force: true });
    };
  }, [mapContainerElement]);

  const route = trip && typeof trip.route === "object" ? trip.route : null;
  const diagnostic = getRouteDiagnostic(route);

  return {
    mapContainerRef,
    mapRenderMeta,
    mapStateRef,
    routeStatus: getRouteStatusMeta(route, diagnostic),
    statusNotice: buildRouteStatusNotice(trip, route, diagnostic, mapRenderMeta),
  };
}
